import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/header/header.component';
import { ResponsavelLegal, ResponsavelModalComponent } from '../../shared/responsavel-modal/responsavel-modal.component';
import { CadastroPacienteModalComponent } from '../../shared/cadastro-paciente-modal/cadastro-paciente-modal.component';
import { PagamentoEspecieModalComponent } from '../../shared/pagamento-especie-modal/pagamento-especie-modal.component';
import { PacienteCadastrado } from '../../../services/patient/patient.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { PatientService } from '../../../services/patient/patient.service';
import { AtendimentoService } from '../../../services/atendimento/atendimento.service';
import { CheckinService } from '../../../services/checkin/checkin.service';
import { DoctorService } from '../../../services/doctor/doctor.service';
import { PasswordService } from '../../../services/password/password.service';
import { AttendanceCycleService } from '../../../services/attendance-cycle/attendance-cycle.service';
import { AssinaturaAtivada, CORES_PLANO, OfertaSimComponent } from './oferta-sim/oferta-sim.component';
import { MelhorarPlanoSimComponent } from './melhorar-plano-sim/melhorar-plano-sim.component';
import { PendenciaSimComponent } from './pendencia-sim/pendencia-sim.component';
import { verificandoCartaoSim } from './verificando-cartaosim';
import { PendenciaCanceladaSimComponent } from './pendencia-cancelada-sim/pendencia-cancelada-sim.component';
import { BloqueioAdesaoSimComponent } from './bloqueio-adesao-sim/bloqueio-adesao-sim.component';
import { BrlPipe } from '../../../utils/brl.pipe';
import { BrandService } from '../../../services/brand/brand.service';
import { AdesaoService } from '../../../services/adesao/adesao.service';
import { ValorVisita } from '../../../interfaces/adesao.dto';
import { AgendaFilaService } from '../../../services/agenda-fila/agenda-fila.service';
import { AgendaDia } from '../../../interfaces/agenda-dia';
import { Convenio } from '../../../interfaces/convenio';
import { CategoriaConvenio } from '../../../interfaces/categoria-convenio';
import { convenioNaLista } from './convenio-na-lista';
import { ehSelecaoParceira, filtrarParceiras, selecaoParceira } from './selecao-parceira';
import { agruparHorarios, HorariosAgrupados } from './encaixe-horarios';
import { ClinicaParceira } from '../../../interfaces/clinica-parceira';
import { SlotDisponivelItem } from '../../../interfaces/slot-disponivel';
import { AbrirAtendimentoRequest, ProcedimentoInput } from '../../../interfaces/abrir-atendimento-request';
import { AbrirAtendimentoResponse } from '../../../interfaces/abrir-atendimento-response';
import { SetorAtendimento } from '../../../interfaces/setor-atendimento';
import { ProcedimentoBusca, ProcedimentoClassificacao, FiltroTipoProcedimento } from '../../../interfaces/procedimento-busca';
import { OrcamentoGroup, OrcamentoItem } from '../../../interfaces/orcamento-item';
import { AtendimentoDuplicadoResult } from '../../../interfaces/atendimento-duplicado-result';
import { DadosPagamento, ProcessarPagamentoRequest, ProcessarPagamentoResult, ItemContaPaciente, CupomDesconto, MotivoDesconto, AdiantamentoDisponivel, ResumoConclusao } from '../../../interfaces/dados-pagamento';
import { ADIANT_DESTAQUE_MS, BuscaAdiantamentos, mesclarAdiantamentos } from '../../../utils/adiantamentos-refresh.util';
import { CashRegisterService } from '../../../services/cash-register/cash-register.service';
import { fichaLogoHtml, fichaTitulo } from '../../../utils/print-receipt.util';
import { TcleEntry, matchTcles, urlTcle } from '../../../utils/tcle-map';
import { imprimirTcle, TcleKey } from '../../../utils/tcle-print.util';
import { parcelaMinima, parcelasDisponiveis as calcularParcelasDisponiveis } from '../../../utils/parcelamento.util';
import { filtrarTerminaisPorTipo, montarTerminalFull, serialInicialPorTipo } from '../../../utils/maquineta.util';
import { Doctor } from '../../../interfaces/doctor';
import { Patient } from '../../../interfaces/patient';
import { QR_CODE_HRA_RECEPCAO_MAP } from '../../../constants/qr-code-hra.constant';
import { TermosPavModalComponent } from '../../shared/termos-pav/termos-pav-modal.component';

// ── Validadores ──────────────────────────────────────────────────────────────

function validarTelefone(ddd: string, cel: string): boolean {
  const digits = (ddd + cel).replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11;
}

function validarEmail(email: string): boolean {
  if (!email || !email.includes('@')) return false;
  const [local, domain] = email.split('@');
  if (!local || local.length < 1) return false;
  if (!domain || !domain.includes('.')) return false;
  const partes = domain.split('.');
  const tld = partes[partes.length - 1];
  const domainName = partes.slice(0, -1).join('.');
  if (tld.length < 2) return false;
  if (domainName.length < 2) return false;
  return /^[a-zA-Z0-9._%+\-]+$/.test(local) && /^[a-zA-Z0-9.\-]+$/.test(domain);
}

function validarCpf(cpf: string): boolean {
  const c = cpf.replace(/\D/g, '');
  if (c.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(c)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(c[i]) * (10 - i);
  let r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  if (r !== parseInt(c[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(c[i]) * (11 - i);
  r = (sum * 10) % 11;
  if (r === 10 || r === 11) r = 0;
  return r === parseInt(c[10]);
}

function formatCep(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

function dateToInputValue(date: Date | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
}

@Component({
  selector: 'app-abrir-atendimento',
  standalone: true,
  imports: [HeaderComponent, CommonModule, FormsModule, ResponsavelModalComponent, CadastroPacienteModalComponent, PagamentoEspecieModalComponent, OfertaSimComponent, MelhorarPlanoSimComponent, PendenciaSimComponent, PendenciaCanceladaSimComponent, BloqueioAdesaoSimComponent, BrlPipe, TermosPavModalComponent],
  templateUrl: './abrir-atendimento.component.html',
  styleUrl: './abrir-atendimento.component.scss',
})
export class AbrirAtendimentoComponent implements OnInit, OnDestroy {

  // ── Dados do paciente / senha ──────────────────────────────
  selectedPassword: any = null;
  mostrarTermos = false;
  clinicId: number = 0;
  userName: string = '';
  isOdonto = false;

  // ── Estado do wizard ───────────────────────────────────────
  stage: 'busca-paciente' | 'validacao' | 'step1' | 'step2' | 'step3' | 'pagamento' = 'validacao';

  // ── Trocar Paciente ────────────────────────────────────────
  showTrocarPaciente = false;
  trocarCpfQuery = '';
  trocarResultados: { cdPessoaFisica: number; nmPessoaFisica: string; nrCpf: string }[] = [];
  trocarPacienteSelecionado: { cdPessoaFisica: number; nmPessoaFisica: string; nrCpf: string } | null = null;
  isLoadingTrocar = false;
  trocarErro = '';
  trocarDebounce: any = null;

  // ── Busca de paciente (Express) ────────────────────────────
  buscaPacienteQuery = '';
  buscaPacienteResultados: { cdPessoaFisica: number; nmPessoaFisica: string; nrCpf: string }[] = [];
  isLoadingBuscaPaciente = false;
  buscaPacienteDebounce: any = null;

  // ── Dados completos do paciente (carregados da API) ────────
  patientDetails: Patient | null = null;
  isLoadingPatient = false;

  // ── Campos de edição do cadastro ───────────────────────────
  nomeEditado = '';
  socialNameEditado = '';
  nascimentoEditado = '';   // YYYY-MM-DD para input[type=date]
  generoEditado = '';
  dddEditado = '';
  celularEditado = '';
  emailEditado = '';
  semEmail = false;

  // Endereço
  cepEditado = '';
  logradouroEditado = '';
  numeroEditado = '';
  complementoEditado = '';
  bairroEditado = '';
  cidadeEditado = '';
  estadoEditado = '';
  isLoadingCep = false;

  // ── Responsável Legal (modal em shared/responsavel-modal) ──
  showModalResponsavel = false;
  respLegalSalvo = false;
  respLegalForm: ResponsavelLegal = {
    nmContato: '', nrCpf: '', nrDdd: '', nrTelefone: '', dsEmail: '',
    dsEndereco: '', nrEndereco: '', dsComplemento: '', dsBairro: '',
    dsMunicipio: '', sgEstado: '', cdCep: '', cdMunicipioIbge: null,
  };

  // ── Avaliação Odontológica Gratuita (modal pós-confirmação) ──
  showAvaliacaoOdontoModal = false;
  agendasOdonto: { cdAgenda: number; nmMedico: string; qtAC: number }[] = [];
  agendaOdontoSelecionada: number | null = null;
  isAgendandoOdonto = false;
  avaliacaoOdontoErro = '';
  avaliacaoOdontoSucesso = '';
  modoJustificativaOdonto = false;
  motivoRecusaOdonto = '';
  avaliacaoOdontoConcluida = false;
  odontoResult: { nrAtendimento?: number; nrPrescricao?: number } | null = null;

  // Legado (mantido só para compatibilidade com código ainda não refatorado)
  nomeResponsavelEditado = '';
  cpfResponsavelEditado = '';

  // Erros de validação
  telefoneErro = '';
  emailErro = '';
  isSavingPatient = false;

  // ── Step 1 ─────────────────────────────────────────────────
  agendas: any[] = [];
  selectedAgenda: any = null;
  setores: SetorAtendimento[] = [];
  cdSetorAtendimento: number | null = null;
  tipoAtendimento: number = 8;
  classificacao: string = 'C';
  isLoadingAgendas = false;
  isLoadingSetores = false;

  // ── Clínica parceira sem agendamento (lista vazia fora da Super) ──
  clinicasParceiras: ClinicaParceira[] = [];
  showParceirasModal = false;
  filtroParceira = '';

  get parceirasFiltradas(): ClinicaParceira[] {
    return filtrarParceiras(this.clinicasParceiras, this.filtroParceira);
  }

  get parceiraSelecionada(): boolean {
    return ehSelecaoParceira(this.selectedAgenda);
  }

  convenios: Convenio[] = [];
  categorias: CategoriaConvenio[] = [];
  selectedCdConvenio: number | null = null;
  selectedCdCategoria: string | null = null;

  // De-para de rótulo de convênio por tenant (só exibição; o cdConvenio vai igual pro back).
  // Super Clínica (cdEmpresa 16): exibe "Super Vida" no lugar de "CARTÃOSiM". Chave em UPPER.
  private readonly convenioDeParaSuperClinica: Record<string, string> = {
    'CARTÃOSIM': 'Super Vida',
  };

  get isSuperClinica(): boolean {
    return this.brandService.cdEmpresa === '16';
  }

  convenioLabel(dsConvenio: string | null | undefined): string {
    const ds = dsConvenio ?? '';
    if (this.isSuperClinica) {
      const mapped = this.convenioDeParaSuperClinica[ds.trim().toUpperCase()];
      if (mapped) return mapped;
    }
    return ds;
  }

  // ── Step 2 — Procedimentos ─────────────────────────────────
  procedimentosBusca = '';
  procedimentosFiltrados: ProcedimentoBusca[] = [];
  showDropdownProcedimentos = false;
  isLoadingProcedimentos = false;
  isLoadingPadrao = false;
  procedimentosAdicionados: {
    nome: string;
    cdProcedimento: number;
    cdMedicoExecutor: number | null;
    nomeMedicoExecutor: string;
    quantidade: number;
    medicoBusca: string;
    medicosFiltrados: Doctor[];
    showMedicoDropdown: boolean;
  }[] = [];

  // ── Médicos (para typeahead do Médico Executor) ────────────
  todosMedicos: Doctor[] = [];
  isLoadingMedicos = false;

  // ── Orçamento ──────────────────────────────────────────────
  isLoadingOrcamentos = false;
  orcamentoGroups: OrcamentoGroup[] = [];
  showOrcamentoModal = false;
  orcamentoModoPagamento = false;
  isInserindoOrcamento = false;

  // ── Atendimento duplicado ───────────────────────────────────
  showDuplicadoModal = false;
  atendimentoDuplicado: AtendimentoDuplicadoResult | null = null;
  isVerificandoDuplicado = false;

  // ── Submit / resultado ─────────────────────────────────────
  isSubmitting = false;
  resultado: AbrirAtendimentoResponse | null = null;
  erroMensagem = '';

  // ── Pagamento ──────────────────────────────────────────────
  copiadoCampo = '';
  showItensConta = false;
  editandoItensConta = false;
  itensConta: ItemContaPaciente[] = [];
  isLoadingItens = false;
  adicionandoProcedimento = false;
  novoProcBusca = '';
  novoProcFiltrados: ProcedimentoBusca[] = [];
  showNovoProcDropdown = false;
  novoProcSelecionado: ProcedimentoBusca | null = null;
  novoProcQtd = 1;
  isAdicionandoProc = false;
  showCupomModal = false;
  cuponsDisponiveis: CupomDesconto[] = [];
  isLoadingCupons = false;
  isAplicandoCupom = false;
  cupomMensagem = '';
  cupomMensagemTipo: 'sucesso' | 'erro' = 'sucesso';
  showDescontoPepModal = false;
  motivosDesconto: MotivoDesconto[] = [];
  isLoadingMotivos = false;
  isAplicandoDesconto = false;
  descontoPepMotivo: number | null = null;
  descontoPepPercentual: number | null = null;
  descontoPepObs = '';
  // ── Maquineta ──────────────────────────────────────────────
  showMaquinetaModal = false;
  maquinetaMetodo: number = 1;  // 1=Crédito, 2=Débito
  maquinetaParcelas: number = 1;
  maquinetaValor: number = 0;
  maquinetaTerminalTipo: string = 'V240mPlus';
  maquinetaTerminalNumero: string = '';
  maquinetaTerminalFull: string = '';
  maquinetaTerminaisFiltrados: { tipo: string; numero: string; loja: string }[] = [];
  showTerminalDropdown = false;
  isEnviandoMaquineta = false;
  aguardandoMaquineta = false;
  dadosPagamento: DadosPagamento | null = null;
  isLoadingPagamento = false;
  isProcessingPagamento = false;
  pagamentoResultado: ProcessarPagamentoResult | null = null;
  pagamentoErro = '';
  pagamentoPulado = false;
  // ── Status da Agenda ──────────────────────────────────────
  statusAgenda: string = 'AR';
  statusAgendaFeedback = '';
  showPularPagamentoModal = false;

  // ── Vincular Adiantamento ──────────────────────────────────
  showVincularModal = false;
  adiantamentosDisponiveis: AdiantamentoDisponivel[] = [];
  adiantamentosSelecionados: Set<number> = new Set();
  isLoadingAdiantamentos = false;
  atualizandoAdiantamentos = false;
  adiantamentosNovos: Set<number> = new Set();
  private destaqueAdiantTimer: ReturnType<typeof setTimeout> | null = null;
  readonly buscaAdiantamentos = new BuscaAdiantamentos(
    () => this.dadosPagamento ? this.atendimentoService.getAdiantamentosDisponiveis(this.dadosPagamento.nrInternoConta) : Promise.resolve([]),
    lista => this.aplicarAdiantamentos(lista),
  );
  isVinculando = false;
  vincularProgresso = '';
  private vincularCallback: (() => Promise<void>) | null = null;

  // ── Encaixe (agendar antes de abrir) ──────────────────────
  showEncaixeModal = false;
  agendasEncaixe: AgendaDia[] = [];
  filtroAgenda = '';
  isLoadingAgendasEncaixe = false;
  agendaEncaixeSelecionada: AgendaDia | null = null;
  slotsDisponiveis: SlotDisponivelItem[] = [];
  todosSlotsEncaixe: SlotDisponivelItem[] = [];
  isLoadingSlotsDisponiveis = false;
  isAgendandoEncaixe = false;
  encaixeErro = '';
  slotEncaixeSelecionado: SlotDisponivelItem | null = null;
  mostrarHorariosAnteriores = false;
  horariosEncaixe: HorariosAgrupados<SlotDisponivelItem> = { grupos: [], anteriores: 0, livres: 0 };

  private atualizarHorariosEncaixe(): void {
    this.horariosEncaixe = agruparHorarios(this.slotsDisponiveis, {
      agora: new Date(),
      mostrarAnteriores: this.mostrarHorariosAnteriores,
    });
    if (this.slotEncaixeSelecionado && !this.horariosEncaixe.grupos.some(g => g.slots.includes(this.slotEncaixeSelecionado!))) {
      this.slotEncaixeSelecionado = null;
    }
  }

  alternarHorariosAnteriores(): void {
    this.mostrarHorariosAnteriores = !this.mostrarHorariosAnteriores;
    this.atualizarHorariosEncaixe();
  }

  confirmarEncaixeSelecionado(): void {
    if (this.slotEncaixeSelecionado) this.confirmarEncaixe(this.slotEncaixeSelecionado);
  }

  get agendasEncaixeFiltradas(): AgendaDia[] {
    const termo = this.filtroAgenda.toLowerCase().trim();
    if (!termo) return this.agendasEncaixe;
    return this.agendasEncaixe.filter(ag =>
      ag.nmMedico.toLowerCase().includes(termo) ||
      ag.dsAgenda.toLowerCase().includes(termo)
    );
  }
  encaixeSucesso = '';

  // ── Step 5 — Conclusão ─────────────────────────────────────
  resumoConclusao: ResumoConclusao | null = null;
  isLoadingResumo = false;
  showHraModal = false;
  qrCodeHraPath: string | null = null;
  showConcluirModal = false;
  isFinalizandoSenha = false;
  // Id da senha ja finalizada — evita finalizar duas vezes.
  private senhaFinalizadaId: number | null = null;
  // Marca que pelo menos um atendimento foi criado para a senha atual (usado no
  // ngOnDestroy para finalizar a senha ao sair da tela sem clicar "Finalizar").
  private atendimentoCriadoNestaSenha = false;
  concluirErro = '';
  tclesEncontrados: TcleEntry[] = [];

  // ── Pagamento em Espécie (Caixa) ───────────────────────────
  // O fluxo mora no PagamentoEspecieModalComponent; aqui só o acesso e a abertura.
  hasCaixaAccess = false;
  showCaixaModal = false;

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private patientService: PatientService,
    private atendimentoService: AtendimentoService,
    private checkinService: CheckinService,
    private doctorService: DoctorService,
    private passwordService: PasswordService,
    private cashRegisterService: CashRegisterService,
    private agendaFilaService: AgendaFilaService,
    private attendanceCycleService: AttendanceCycleService,
    private brandService: BrandService,
    private adesaoService: AdesaoService,
  ) {}

  ngOnInit(): void {
    this.selectedPassword = this.localStorageService.get('selectedPassword');
    this.clinicId = Number(this.localStorageService.get('currentUnit'));
    this.userName = this.localStorageService.get('login') ?? '';
    this.isOdonto = this.localStorageService.get('profile') === 'OdontoSiM';

    const qrFile = this.isSuperClinica ? undefined : QR_CODE_HRA_RECEPCAO_MAP[this.clinicId];
    this.qrCodeHraPath = qrFile ? `assets/images/qrCodes/${qrFile}` : null;

    this.carregarCaixasUsuario();
    this.carregarClinicasParceiras();

    if (!this.selectedPassword?.personId) {
      this.stage = 'busca-paciente';
      return;
    }

    this.carregarDadosPaciente();
    this.carregarAgendas();
    this.carregarConvenios();
  }

  private async carregarCaixasUsuario(): Promise<void> {
    try {
      this.hasCaixaAccess = !!this.userName && !!this.clinicId
        && (await this.cashRegisterService.getByUnit(this.clinicId, this.userName)).length > 0;
    } catch {
      this.hasCaixaAccess = false;
    }
  }

  private async carregarClinicasParceiras(): Promise<void> {
    if (!this.clinicId) return;
    this.clinicasParceiras = await this.atendimentoService.getClinicasParceiras(this.clinicId);
  }

  abrirParceirasModal(): void {
    this.filtroParceira = '';
    this.showParceirasModal = true;
  }

  escolherParceira(c: ClinicaParceira): void {
    this.showParceirasModal = false;
    this.selectAgenda(selecaoParceira(c));
  }

  abrirCaixaModal(): void {
    if (!this.hasCaixaAccess || !this.dadosPagamento) return;
    this.showCaixaModal = true;
  }

  // Recarrega a conta: o adiantamento novo faz o "Processar Pagamento" abrir o vincular.
  async onEspecieRegistrado(): Promise<void> {
    await this.recarregarDadosPagamento();
  }

  // ── Fechar dropdown ao clicar fora ────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.procedimento-search-container')) {
      this.showDropdownProcedimentos = false;
    }
    if (!target.closest('.medico-search-container')) {
      this.procedimentosAdicionados.forEach(p => p.showMedicoDropdown = false);
    }
  }

  // ── Cadastro de paciente (CPF que não existe no TASY) ────────
  showCadastroPaciente = false;
  cadastroCpfInicial = '';
  private cadastroOrigem: 'express' | 'trocar' | 'senha' = 'express';

  abrirCadastroPaciente(origem: 'express' | 'trocar' | 'senha', termo: string): void {
    this.cadastroOrigem = origem;
    const digitos = (termo ?? '').replace(/\D/g, '');
    this.cadastroCpfInicial = digitos.length === 11 ? digitos : '';
    this.showCadastroPaciente = true;
  }

  /** Senha chegou com CPF que não existe no TASY: sem pessoa para editar. */
  get pacienteSemCadastro(): boolean {
    return this.stage === 'validacao' && !this.isLoadingPatient && !this.patientDetails && !this.selectedPassword?.personId;
  }

  // Cadastrado (ou já existente): segue pelo mesmo caminho de quem foi achado na busca.
  async onPacienteCadastrado(paciente: PacienteCadastrado): Promise<void> {
    this.showCadastroPaciente = false;
    const selecionado = { cdPessoaFisica: paciente.cdPessoaFisica, nmPessoaFisica: paciente.nome, nrCpf: paciente.cpf };

    if (this.cadastroOrigem === 'trocar') {
      this.selecionarTrocar(selecionado);
      return;
    }
    this.erroMensagem = '';
    await this.selecionarPacienteBusca(selecionado);
  }

  // ── Busca de paciente (Express) ────────────────────────────

  onBuscaPacienteInput(): void {
    clearTimeout(this.buscaPacienteDebounce);
    const termo = this.buscaPacienteQuery.trim();
    if (termo.length < 9) {
      this.buscaPacienteResultados = [];
      return;
    }
    this.buscaPacienteDebounce = setTimeout(() => this.executarBuscaPaciente(termo), 400);
  }

  private async executarBuscaPaciente(termo: string): Promise<void> {
    this.isLoadingBuscaPaciente = true;
    try {
      this.buscaPacienteResultados = await this.atendimentoService.buscarPaciente(termo);
    } catch {
      this.buscaPacienteResultados = [];
    } finally {
      this.isLoadingBuscaPaciente = false;
    }
  }

  // ── Trocar Paciente ────────────────────────────────────────

  abrirTrocarPaciente(): void {
    this.showTrocarPaciente = true;
    this.trocarCpfQuery = '';
    this.trocarResultados = [];
    this.trocarPacienteSelecionado = null;
    this.trocarErro = '';
  }

  fecharTrocarPaciente(): void {
    this.showTrocarPaciente = false;
  }

  onTrocarCpfInput(): void {
    clearTimeout(this.trocarDebounce);
    const termo = this.trocarCpfQuery.trim();
    this.trocarPacienteSelecionado = null;
    if (termo.replace(/\D/g, '').length < 3) { this.trocarResultados = []; return; }
    this.trocarDebounce = setTimeout(() => this.executarBuscaTrocar(termo), 400);
  }

  async executarBuscaTrocar(termo: string): Promise<void> {
    this.isLoadingTrocar = true;
    try {
      this.trocarResultados = await this.atendimentoService.buscarPaciente(termo);
    } catch { this.trocarResultados = []; }
    finally { this.isLoadingTrocar = false; }
  }

  selecionarTrocar(p: { cdPessoaFisica: number; nmPessoaFisica: string; nrCpf: string }): void {
    this.trocarPacienteSelecionado = p;
    this.trocarResultados = [];
    this.trocarCpfQuery = p.nmPessoaFisica;
  }

  async confirmarTrocarPaciente(): Promise<void> {
    if (!this.trocarPacienteSelecionado || !this.selectedPassword?.id) return;
    this.isLoadingTrocar = true;
    this.trocarErro = '';
    try {
      await this.passwordService.swapPatient(
        Number(this.selectedPassword.id),
        this.trocarPacienteSelecionado.cdPessoaFisica,
        this.clinicId,
        this.userName,
      );
      this.selectedPassword = {
        ...this.selectedPassword,
        personId: this.trocarPacienteSelecionado.cdPessoaFisica,
        name: this.trocarPacienteSelecionado.nmPessoaFisica,
        cpf: this.trocarPacienteSelecionado.nrCpf,
      };
      this.localStorageService.set('selectedPassword', this.selectedPassword);
      this.showTrocarPaciente = false;
      this.carregarDadosPaciente();
      this.carregarAgendas();
    } catch (err: any) {
      this.trocarErro = err?.error?.feedback ?? 'Erro ao trocar paciente. Tente novamente.';
    } finally {
      this.isLoadingTrocar = false;
    }
  }

  // ── Busca de paciente (Express) ────────────────────────────

  async selecionarPacienteBusca(paciente: { cdPessoaFisica: number; nmPessoaFisica: string; nrCpf: string }): Promise<void> {
    let senhaOverride: { id: any; idQueue: any; dsPassword: any } | null = null;

    if (this.selectedPassword?.id) {
      try {
        const linkResponse = await this.passwordService.linkPassword(
          this.userName,
          0,
          Number(this.selectedPassword.id),
          paciente.cdPessoaFisica,
          this.clinicId,
          0,
          '',
        );
        // Se o backend retornou uma senha existente, usá-la no lugar da express
        if (linkResponse?.data?.[0]) {
          senhaOverride = linkResponse.data[0];
        }
      } catch { /* não bloqueia */ }
    }

    this.selectedPassword = {
      ...this.selectedPassword,
      ...(senhaOverride
        ? { id: senhaOverride.id, idQueue: senhaOverride.idQueue, dsPassword: senhaOverride.dsPassword }
        : {}),
      personId: paciente.cdPessoaFisica,
      name: paciente.nmPessoaFisica,
      cpf: paciente.nrCpf,
    };
    this.localStorageService.set('selectedPassword', this.selectedPassword);

    this.stage = 'validacao';
    this.carregarDadosPaciente();
  }

  // ── Dados completos do paciente ────────────────────────────

  async carregarDadosPaciente(): Promise<void> {
    this.isLoadingPatient = true;
    try {
      const cpf = (this.selectedPassword?.cpf ?? '').replace(/\D/g, '');

      if (cpf.length === 11) {
        // person-by-document retorna dados completos: phone, email, gender, socialName, address, responsible
        try {
          this.patientDetails = await this.patientService.getPersonByDocument(cpf);
        } catch { /* tenta fallback */ }
      }

      // Fallback: busca por personId (código do paciente)
      if (!this.patientDetails && this.selectedPassword?.personId) {
        try {
          this.patientDetails = await this.patientService.getPersonByFilter({
            type: 2,
            filter: String(this.selectedPassword.personId),
          });
        } catch { /* não bloqueia */ }
      }

      if (this.patientDetails) {
        this.popularCamposEdicao();
      } else {
        this.nomeEditado = this.selectedPassword?.name ?? '';
        this.erroMensagem = 'Não foi possível carregar os dados do paciente. Preencha os campos manualmente.';
      }
    } catch {
      this.nomeEditado = this.selectedPassword?.name ?? '';
      this.erroMensagem = 'Erro ao carregar dados do paciente.';
    } finally {
      this.isLoadingPatient = false;
    }
  }

  private popularCamposEdicao(): void {
    if (!this.patientDetails) return;
    this.nomeEditado       = this.selectedPassword?.name ?? this.patientDetails.name ?? '';
    this.socialNameEditado = this.patientDetails.socialName ?? '';
    this.generoEditado     = this.patientDetails.gender ?? '';
    this.nascimentoEditado = dateToInputValue(this.patientDetails.birthDate);
    this.emailEditado      = this.patientDetails.email ?? '';

    const phone = (this.patientDetails.phone ?? '').replace(/\D/g, '');
    this.dddEditado    = phone.slice(0, 2);
    this.celularEditado = phone.slice(2);

    const addr = this.patientDetails.address;
    this.cepEditado         = addr?.zipcode ?? '';
    this.logradouroEditado  = addr?.street ?? '';
    this.numeroEditado      = addr?.number ? String(addr.number) : '';
    this.complementoEditado = addr?.complement ?? '';
    this.bairroEditado      = addr?.neighborhood ?? '';
    this.cidadeEditado      = addr?.city ?? '';
    this.estadoEditado      = addr?.state ?? '';

    this.nomeResponsavelEditado = this.patientDetails.responsibleName ?? '';
    this.cpfResponsavelEditado  = this.patientDetails.responsibleDocument ?? '';
  }

  private replicarDadosDoResponsavel(): void {
    const r = this.respLegalForm;
    if (!this.emailEditado      && r.dsEmail)     this.emailEditado      = r.dsEmail;
    if (!this.cepEditado        && r.cdCep)       this.cepEditado        = r.cdCep;
    if (!this.logradouroEditado && r.dsEndereco)  this.logradouroEditado = r.dsEndereco;
    if (!this.numeroEditado     && r.nrEndereco)  this.numeroEditado     = String(r.nrEndereco);
    if (!this.complementoEditado && r.dsComplemento) this.complementoEditado = r.dsComplemento;
    if (!this.bairroEditado     && r.dsBairro)    this.bairroEditado     = r.dsBairro;
    if (!this.cidadeEditado     && r.dsMunicipio) this.cidadeEditado     = r.dsMunicipio;
    if (!this.estadoEditado     && r.sgEstado)    this.estadoEditado     = r.sgEstado;
    if (!this.dddEditado        && r.nrDdd)       this.dddEditado        = r.nrDdd;
    if (!this.celularEditado    && r.nrTelefone)  this.celularEditado    = r.nrTelefone;
  }

  // ── Menor de idade ─────────────────────────────────────────

  get ehMenorDeIdade(): boolean {
    // Prioridade: usar age do patientDetails (vem da API com idade real)
    if (this.patientDetails?.age != null) return this.patientDetails.age < 18;
    // Fallback: usar age da senha (fila)
    const age = this.selectedPassword?.age;
    if (age != null) return age < 18;
    return false;
  }

  // ── CEP — auto-fill ViaCEP ─────────────────────────────────

  onCepInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    this.cepEditado = formatCep(digits);
    input.value = this.cepEditado;
    if (digits.length === 8) {
      this.buscarCep(digits);
    }
  }

  private async buscarCep(cep: string): Promise<void> {
    this.isLoadingCep = true;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        this.logradouroEditado = data.logradouro || this.logradouroEditado;
        this.bairroEditado     = data.bairro     || this.bairroEditado;
        this.cidadeEditado     = data.localidade  || this.cidadeEditado;
        this.estadoEditado     = data.uf          || this.estadoEditado;
      }
    } catch { /* silencioso */ }
    finally { this.isLoadingCep = false; }
  }

  // ── Inputs com filtro numérico ─────────────────────────────

  onDddInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.dddEditado = input.value.replace(/\D/g, '').slice(0, 2);
    input.value = this.dddEditado;
    this.telefoneErro = '';
  }

  onCelularInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.celularEditado = input.value.replace(/\D/g, '').slice(0, 9);
    input.value = this.celularEditado;
    this.telefoneErro = '';
  }

  // ── Modal Responsável Legal (componente em shared/responsavel-modal) ──

  abrirModalResponsavel(): void {
    this.showModalResponsavel = true;
  }

  onResponsavelCarregado(e: { responsavel: ResponsavelLegal; completo: boolean }): void {
    this.respLegalForm = e.responsavel;
    this.respLegalSalvo = e.completo;
    this.replicarDadosDoResponsavel();
  }

  onResponsavelSalvo(r: ResponsavelLegal): void {
    this.respLegalForm = r;
    this.respLegalSalvo = true;
    this.replicarDadosDoResponsavel();
  }

  // ── Salvar cadastro e avançar para Step 1 ─────────────────

  // Erros de endereço
  enderecoErro = '';

  async salvarEContinuar(): Promise<void> {
    this.telefoneErro    = '';
    this.emailErro       = '';
    this.enderecoErro    = '';
    this.erroMensagem    = '';

    // Nome obrigatório
    if (!this.nomeEditado.trim()) {
      this.erroMensagem = 'O nome do paciente é obrigatório.';
      return;
    }

    // Validar telefone (obrigatório)
    if (!this.dddEditado || !this.celularEditado) {
      this.telefoneErro = 'Telefone é obrigatório. Informe DDD e celular.';
    } else if (!validarTelefone(this.dddEditado, this.celularEditado)) {
      this.telefoneErro = 'Informe DDD (2 dígitos) e celular (8 ou 9 dígitos).';
    }

    // Validar e-mail (obrigatório e formato, exceto se "Não possui Email" marcado)
    if (!this.semEmail) {
      if (!this.emailEditado.trim()) {
        this.emailErro = 'E-mail é obrigatório.';
      } else if (!validarEmail(this.emailEditado)) {
        this.emailErro = 'E-mail inválido. Informe um endereço real (ex: nome@dominio.com).';
      }
    }

    // Validar endereço (obrigatório)
    const camposFaltando: string[] = [];
    if (!this.cepEditado.replace(/\D/g, ''))    camposFaltando.push('CEP');
    if (!this.logradouroEditado.trim())          camposFaltando.push('Logradouro');
    if (!this.numeroEditado.trim())              camposFaltando.push('Número');
    if (!this.bairroEditado.trim())              camposFaltando.push('Bairro');
    if (!this.cidadeEditado.trim())              camposFaltando.push('Cidade');
    if (!this.estadoEditado.trim())              camposFaltando.push('Estado');
    if (camposFaltando.length > 0) {
      this.enderecoErro = `Campos obrigatórios: ${camposFaltando.join(', ')}.`;
    }

    // Validar responsável legal — obrigatório para menor, salvo via modal
    if (this.ehMenorDeIdade && !this.respLegalSalvo) {
      this.erroMensagem = 'Paciente menor de idade: cadastre o Responsável Legal antes de continuar.';
      this.abrirModalResponsavel();
      return;
    }

    if (this.telefoneErro || this.emailErro || this.enderecoErro) return;

    // Salvar no TASY se tivermos os dados completos
    if (this.patientDetails) {
      this.isSavingPatient = true;
      try {
        const phoneDigits = (this.dddEditado + this.celularEditado).replace(/\D/g, '');
        const atualizado: Patient = {
          ...this.patientDetails,
          name:                this.nomeEditado.trim() || this.patientDetails.name,
          socialName:          this.socialNameEditado.trim(),
          phone:               phoneDigits,
          email:               this.semEmail ? '' : this.emailEditado.trim(),
          gender:              this.generoEditado,
          birthDate:           this.nascimentoEditado ? new Date(this.nascimentoEditado) : this.patientDetails.birthDate,
          // Responsável legal agora é salvo pelo modal em endpoint separado —
          // não enviar pelo updatePatient para não disparar o fluxo legado
          responsibleName:     '',
          responsibleDocument: '',
          address: {
            zipcode:      this.cepEditado.replace(/\D/g, ''),
            street:       this.logradouroEditado.trim(),
            number:       Number(this.numeroEditado) || 0,
            complement:   this.complementoEditado.trim(),
            neighborhood: this.bairroEditado.trim(),
            city:         this.cidadeEditado.trim(),
            state:        this.estadoEditado.trim().toUpperCase().slice(0, 2),
          },
        };
        await this.patientService.updatePatient(atualizado);
        this.patientDetails = atualizado;
        if (this.nomeEditado.trim()) {
          this.selectedPassword = { ...this.selectedPassword, name: this.nomeEditado.trim() };
        }
      } catch (err) {
        console.error('[abrir-atendimento] Erro ao salvar cadastro do paciente/responsável:', err);
        this.erroMensagem = 'Não foi possível salvar o cadastro. Verifique os dados e tente novamente.';
        this.isSavingPatient = false;
        return;
      } finally {
        this.isSavingPatient = false;
      }
    } else if (this.nomeEditado.trim()) {
      this.selectedPassword = { ...this.selectedPassword, name: this.nomeEditado.trim() };
    }

    // Avançar para Step 1
    this.erroMensagem = '';
    this.stage = 'step1';
    this.carregarAgendas();
    this.carregarSetores();
  }

  // ── Modal Encaixe ──────────────────────────────────────────

  async abrirModalEncaixe(): Promise<void> {
    this.showEncaixeModal = true;
    this.agendaEncaixeSelecionada = null;
    this.slotsDisponiveis = [];
    this.encaixeErro = '';
    this.encaixeSucesso = '';
    this.filtroAgenda = '';
    this.isLoadingAgendasEncaixe = true;
    try {
      if (this.isOdonto) {
        this.todosSlotsEncaixe = await this.agendaFilaService.getSlotsDisponiveis(this.clinicId, undefined, 98);
        const map = new Map<number, AgendaDia>();
        for (const s of this.todosSlotsEncaixe) {
          let ag = map.get(s.cdAgenda);
          if (!ag) {
            ag = { cdAgenda: s.cdAgenda, dsAgenda: s.dsAgenda, nmMedico: s.nmMedico, qtAgendados: 0, isOnline: 0 };
            map.set(s.cdAgenda, ag);
          }
          ag.qtAgendados++;
        }
        this.agendasEncaixe = Array.from(map.values());
      } else {
        // Perfil normal: inclui agendas de odonto (98/64) no encaixe.
        this.agendasEncaixe = await this.agendaFilaService.getAgendasDia(this.clinicId, undefined, false);
      }
    } catch {
      this.agendasEncaixe = [];
    } finally {
      this.isLoadingAgendasEncaixe = false;
    }
  }

  fecharModalEncaixe(): void {
    if (this.isAgendandoEncaixe) return;
    this.showEncaixeModal = false;
  }

  async selecionarAgendaEncaixe(agenda: AgendaDia): Promise<void> {
    this.agendaEncaixeSelecionada = agenda;
    this.slotsDisponiveis = [];
    this.slotEncaixeSelecionado = null;
    this.mostrarHorariosAnteriores = false;
    this.encaixeErro = '';
    this.isLoadingSlotsDisponiveis = true;
    try {
      if (this.isOdonto) {
        this.slotsDisponiveis = this.todosSlotsEncaixe.filter(s => s.cdAgenda === agenda.cdAgenda);
        if (this.slotsDisponiveis.length === 0) this.encaixeErro = 'Nenhum horário disponível para esta agenda.';
      } else {
        this.slotsDisponiveis = await this.agendaFilaService.getSlotsDisponiveis(this.clinicId, agenda.cdAgenda);
        if (this.slotsDisponiveis.length === 0) this.encaixeErro = 'Nenhum horário disponível para esta agenda.';
      }
    } catch {
      this.encaixeErro = 'Erro ao carregar horários disponíveis.';
    } finally {
      this.atualizarHorariosEncaixe();
      this.isLoadingSlotsDisponiveis = false;
    }
  }

  async confirmarEncaixe(slot: SlotDisponivelItem): Promise<void> {
    if (this.isAgendandoEncaixe) return;
    this.isAgendandoEncaixe = true;
    this.encaixeErro = '';
    try {
      await this.agendaFilaService.agendarPacienteSlot(
        slot.nrSequencia,
        this.selectedPassword.personId,
        this.userName,
      );
      this.encaixeSucesso = `Paciente agendado às ${slot.hrAgenda} com ${slot.nmMedico}.`;
      setTimeout(async () => {
        this.showEncaixeModal = false;
        this.encaixeSucesso = '';
        await this.carregarAgendas();
      }, 1500);
    } catch (e: any) {
      this.encaixeErro = e?.error?.feedback || e?.message || 'Erro ao agendar. Tente novamente.';
    } finally {
      this.isAgendandoEncaixe = false;
    }
  }

  async voltarAgendasEncaixe(): Promise<void> {
    this.agendaEncaixeSelecionada = null;
    this.slotsDisponiveis = [];
    this.slotEncaixeSelecionado = null;
    this.encaixeErro = '';
    this.filtroAgenda = '';
  }

  async voltarDashboard(): Promise<void> {
    // Verifica HRA antes de concluir (apenas aviso, não bloqueia). Super Clínica não usa HRA.
    if (!this.isSuperClinica && this.resultado && this.selectedPassword?.personId && !this.showHraModal) {
      const hraRespondido = await this.atendimentoService.verificarHra(this.selectedPassword.personId);
      if (!hraRespondido) {
        this.showHraModal = true;
        return;
      }
    }
    this.showHraModal = false;

    // Atendimento aberto → pergunta se quer finalizar ou abrir novo atendimento
    if (this.resultado && this.selectedPassword?.id) {
      this.concluirErro = '';
      this.showConcluirModal = true;
      return;
    }

    this.router.navigate(['/home']);
  }

  async confirmarFinalizar(): Promise<void> {
    if (this.isFinalizandoSenha) return;
    this.isFinalizandoSenha = true;
    this.concluirErro = '';
    const senhaId = Number(this.selectedPassword.id);
    console.log('[FinalizarSenha] chamando PUT /Password →', { senhaId, clinicId: this.clinicId, usuario: this.userName });
    try {
      if (this.senhaFinalizadaId !== senhaId) {
        await this.passwordService.finishPassword(
          this.userName,
          0,
          senhaId,
          this.clinicId,
        );
        this.senhaFinalizadaId = senhaId;
        console.log('[FinalizarSenha] ✓ senha encerrada com sucesso, id=', senhaId);
      } else {
        console.log('[FinalizarSenha] senha ja finalizada na criacao, id=', senhaId);
      }
      this.passwordService.setData({ finalizedId: senhaId });
      sessionStorage.setItem('finalizedPasswordId', String(senhaId));
      this.localStorageService.remove('selectedPassword');
      try { await this.attendanceCycleService.finish('ATENDIDO'); } catch {}
      this.showConcluirModal = false;
      this.router.navigate(['/home']);
    } catch (err: any) {
      console.error('[FinalizarSenha] ✗ erro ao encerrar senha id=', senhaId, '| erro=', err);
      this.concluirErro = 'Erro ao encerrar a senha. Tente novamente.';
    } finally {
      this.isFinalizandoSenha = false;
    }
  }

  confirmarNovoAtendimento(): void {
    this.showConcluirModal = false;
    this.resultado = null;
    this.erroMensagem = '';
    // Novo atendimento parte limpo: sem os procedimentos da abertura anterior
    this.procedimentosAdicionados = [];
    this.procedimentosBusca = '';
    this.procedimentosFiltrados = [];
    this.showDropdownProcedimentos = false;
    this.stage = 'step1';
    this.carregarAgendas();
    this.carregarSetores();
  }

  // ── Step 1 — Dados ─────────────────────────────────────────

  async carregarAgendas(): Promise<void> {
    this.isLoadingAgendas = true;
    this.agendas = [];
    this.selectedAgenda = null;
    try {
      const data = await this.patientService.getSchedulesByPatient(
        this.selectedPassword.personId,
        this.clinicId,
      );
      this.agendas = Array.isArray(data) ? data : [];
      // Odonto: mostra apenas agendamentos de odonto (esp 98/64 ou nome odonto/dentista).
      if (this.isOdonto) {
        this.agendas = this.agendas.filter(a =>
          a.specialtyId === 98 || a.specialtyId === 64 ||
          /ODONT|DENT/i.test((a.specialtyName ?? '').toString())
        );
      }
      const primeiraDisponivel = this.agendas.find(a => !a.nrAtendimento);
      if (primeiraDisponivel) {
        this.selectAgenda(primeiraDisponivel);
      }
    } catch (e: any) {
      // handleError do PatientService troca o erro por texto; o 404 e paciente sem agendamento hoje.
      if (!String(e).startsWith('Not Found')) {
        this.erroMensagem = 'Não foi possível carregar os agendamentos do paciente. Tente de novo em instantes.';
      }
    } finally {
      this.isLoadingAgendas = false;
    }
  }

  async carregarSetores(): Promise<void> {
    this.isLoadingSetores = true;
    this.setores = [];
    this.cdSetorAtendimento = null;
    try {
      this.setores = await this.atendimentoService.getSetores(this.clinicId);
    } catch (e: any) {
      this.erroMensagem = e?.message ?? 'Erro ao carregar setores de atendimento.';
    } finally {
      this.isLoadingSetores = false;
    }
  }

  async avancarStep2(): Promise<void> {
    if (!this.selectedAgenda) {
      this.erroMensagem = this.clinicasParceiras.length
        ? 'Selecione um agendamento ou uma clínica parceira para continuar.'
        : 'Selecione um agendamento para continuar.';
      return;
    }
    // Sem agendamento não há convênio de onde herdar: o back recusa (422) sem os dois.
    if (this.parceiraSelecionada && (this.selectedCdConvenio == null || this.selectedCdCategoria == null)) {
      this.erroMensagem = 'Selecione o convênio e a categoria para atender na clínica parceira.';
      return;
    }
    if (!this.cdSetorAtendimento) {
      this.erroMensagem = 'Selecione o Setor de Atendimento para continuar.';
      return;
    }
    this.erroMensagem = '';

    // Avaliacao odonto: NAO oferta no fluxo do perfil odonto.
    // Para os demais perfis, dispara o modal ANTES de entrar no Step 2;
    // se abrir, o fluxo pausa ate o usuario decidir (Sim/Nao).
    if (!this.isOdonto) {
      const abriuModal = await this.verificarAvaliacaoOdonto();
      if (abriuModal) return;
    }

    this.stage = 'step2';
    this.carregarMedicos();
    this.carregarProcedimentoPadrao();
  }

  // ── Oferta SiM+ (step 3) ────────────────────────────────────
  // A oferta mora no app-oferta-sim; aqui ficam só o que o card do paciente mostra e a
  // troca de convênio quando o pagamento ativa a assinatura.
  valorParticularVisita: number | null = null;
  assinaturaAtivada: AssinaturaAtivada | null = null;

  // Conferido no banco (API_BENEFITS_PLAN): a modalidade PRIVATE e o convenio 86, e todo
  // plano CartaoSiM cai no 224. Comparar por codigo, nunca por descricao: existem dez
  // convenios com "PARTICULAR" no nome e o do CartaoSiM tem acento ("CARTAO DE BENEFICIOS").
  private readonly CONVENIO_PARTICULAR = 86;

  get convenioParticular(): boolean {
    return Number(this.selectedCdConvenio) === this.CONVENIO_PARTICULAR;
  }

  get cpfDoPaciente(): string {
    return (this.patientDetails?.document ?? this.selectedPassword?.cpf ?? '').replace(/\D/g, '');
  }

  get procedimentosDaOferta(): { cdProcedimento: number; quantidade: number }[] {
    return this.procedimentosAdicionados.map(p => ({
      cdProcedimento: Number(p.cdProcedimento), quantidade: Number(p.quantidade) || 1,
    }));
  }

  get guicheAtual(): string {
    return String(this.localStorageService.get('idTicketWindow') ?? '');
  }

  get corDoAssinante(): { fundo: string; texto: string } | null {
    return this.assinaturaAtivada ? CORES_PLANO[this.assinaturaAtivada.familia.familia] ?? null : null;
  }

  async onAssinaturaAtivada(ativada: AssinaturaAtivada): Promise<void> {
    this.assinaturaAtivada = ativada;
    await this.aplicarConvenioAssinante(ativada.cdConvenio, ativada.cdCategoria);
    // O resumo le o convenio do valor calculado; sem recalcular, seguia mostrando o particular.
    await this.calcularValorVisita();
  }

  temPendenciaSim: boolean | null = null;
  temCanceladaSim: boolean | null = null;
  /** null enquanto não se sabe: a oferta só aparece com false, para não piscar antes do aviso. */
  temBloqueioSim: boolean | null = null;

  get verificandoCartaoSim(): boolean {
    return verificandoCartaoSim(Number(this.selectedPassword?.personId) || 0, this.temPendenciaSim, this.temCanceladaSim, this.temBloqueioSim);
  }

  // Pagou a fatura unificada: o banco reativou o cliente; aplica o convênio do plano como na adesão.
  async onRegularizado(): Promise<void> {
    try {
      const personId = Number(this.selectedPassword?.personId);
      if (!personId) return;
      const status = await this.adesaoService.ativacao(personId);
      if (status.situacao === 'ativo') await this.aplicarConvenioAssinante(status.cdConvenio, status.cdCategoria);
      this.calcularValorVisita();
    } catch (e) {
      console.error('Falha ao aplicar regularização do CartãoSiM', e);
    }
  }

  // So depois de o banco confirmar a ativacao, e com o convenio/categoria que o proprio
  // TASY da ao paciente — nunca por resposta otimista da API nem por nome de convenio.
  private async aplicarConvenioAssinante(cdConvenio: number | null, cdCategoria: number | null): Promise<void> {
    if (!cdConvenio) return;

    this.selectedCdConvenio = Number(cdConvenio);
    await this.carregarCategorias(this.selectedCdConvenio);

    if (cdCategoria) this.selectedCdCategoria = String(cdCategoria);
  }

  avancarStep3(): void {
    if (this.procedimentosAdicionados.length === 0) {
      this.erroMensagem = 'Adicione pelo menos um procedimento para continuar.';
      return;
    }
    this.erroMensagem = '';
    this.assinaturaAtivada = null;
    this.temPendenciaSim = null;
    this.temCanceladaSim = null;
    this.temBloqueioSim = null;
    this.stage = 'step3';
    this.calcularValorVisita();
  }

  // ── Resumo do step 3: convênio e valor que a conta vai ter ─
  valorVisita: ValorVisita | null = null;
  isCalculandoValor = false;

  private async calcularValorVisita(): Promise<void> {
    const personId = Number(this.selectedPassword?.personId);
    this.valorVisita = null;
    if (!personId) return;
    this.isCalculandoValor = true;
    this.valorVisita = await this.adesaoService.valorVisita(
      this.clinicId, personId,
      this.selectedCdConvenio != null ? Number(this.selectedCdConvenio) : null,
      this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : null,
      this.procedimentosDaOferta,
    );
    this.isCalculandoValor = false;
  }

  /** Nome do convênio que vai para a conta; o do totem só se a tela não tiver nenhum. */
  get convenioResumo(): string {
    const cd = this.valorVisita?.cdConvenio ?? this.selectedCdConvenio;
    const convenio = this.convenios.find(c => Number(c.cdConvenio) === Number(cd));
    if (!convenio) return this.selectedPassword?.planDescription || '—';
    const categoria = String(this.valorVisita?.cdCategoria ?? this.selectedCdCategoria ?? '');
    const dsCategoria = this.categorias.find(c => String(c.cdCategoria) === categoria)?.dsCategoria;
    return [this.convenioLabel(convenio.dsConvenio), dsCategoria].filter(Boolean).join(' · ');
  }

  voltarStep(destino: 'validacao' | 'step1' | 'step2'): void {
    this.erroMensagem = '';
    // Ao voltar do step2, limpa procedimentos para recarregar o padrão na próxima entrada
    if (this.stage === 'step2' && destino === 'step1') {
      this.procedimentosAdicionados = [];
    }
    this.stage = destino;
  }

  selectAgenda(agenda: any): void {
    this.selectedAgenda = agenda;
    this.erroMensagem = '';

    this.tipoAtendimento = 8;
    this.classificacao = ehSelecaoParceira(agenda) ? 'E' : agenda?.scheduleClassification === 'R' ? 'R' : 'C';

    this.selectedCdConvenio = agenda?.cdConvenio != null ? Number(agenda.cdConvenio) : null;
    this.selectedCdCategoria = agenda?.cdCategoria != null ? String(agenda.cdCategoria) : null;
    this.categorias = [];
    this.carregarConvenios();
    if (this.selectedCdConvenio != null) {
      this.carregarCategorias(this.selectedCdConvenio);
    }
    // Parceira: a lista segue o médico da agenda; o plano do paciente quase nunca está nela.
    if (!ehSelecaoParceira(agenda)) this.preencherPlanoDoPaciente(agenda);
  }

  // A trigger SIM_ALTERACAO_CONV_EUP_V2 grava o plano real do paciente quando o convênio
  // é vazio, 86 ou 224: a tela já mostra esse, senão o step 3 cota um e a conta sai com outro.
  private async preencherPlanoDoPaciente(agenda: any): Promise<void> {
    const cd = this.selectedCdConvenio;
    if (cd != null && cd !== this.CONVENIO_PARTICULAR && cd !== 224) return;
    const personId = Number(this.selectedPassword?.personId);
    if (!personId) return;

    const plano = await this.adesaoService.planoPaciente(personId);
    if (!plano || this.selectedAgenda !== agenda) return;
    await this.aplicarConvenioAssinante(plano.cdConvenio, plano.cdCategoria);
  }

  private conveniosSeq = 0;

  private get nrSeqAgendamentoCtx(): number | null {
    return this.selectedAgenda?.id != null ? Number(this.selectedAgenda.id) : null;
  }

  private get cdAgendaCtx(): number | null {
    return ehSelecaoParceira(this.selectedAgenda) ? this.selectedAgenda.cdAgendaParceira : null;
  }

  async carregarConvenios(): Promise<void> {
    const seq = ++this.conveniosSeq;
    try {
      const lista = await this.atendimentoService.getConvenios({
        cdEstabelecimento: this.clinicId,
        nrSeqAgendamento: this.nrSeqAgendamentoCtx,
        cdAgenda: this.cdAgendaCtx,
      });
      if (seq !== this.conveniosSeq) return;
      this.convenios = lista;
      // Convênio da agenda fora da lista (ex.: Super filtra por unidade/médico): o operador escolhe, senão a API devolve 422.
      if (this.selectedCdConvenio != null && !convenioNaLista(lista, this.selectedCdConvenio)) {
        this.selectedCdConvenio = null;
        this.selectedCdCategoria = null;
        this.categorias = [];
      }
      if (this.parceiraSelecionada && this.selectedCdConvenio == null && lista.length === 1) {
        this.selectedCdConvenio = Number(lista[0].cdConvenio);
        await this.carregarCategorias(this.selectedCdConvenio);
      }
    } catch {
      if (seq === this.conveniosSeq) this.convenios = [];
    }
  }

  async carregarCategorias(cdConvenio: number): Promise<void> {
    try {
      const lista = await this.atendimentoService.getCategorias(cdConvenio, this.clinicId);
      if (Number(this.selectedCdConvenio) !== Number(cdConvenio)) return;
      this.categorias = lista;
      if (this.parceiraSelecionada && this.selectedCdCategoria == null && lista.length === 1) {
        this.selectedCdCategoria = String(lista[0].cdCategoria);
      }
    } catch {
      this.categorias = [];
    }
  }

  async onConvenioChange(): Promise<void> {
    this.selectedCdCategoria = null;
    this.categorias = [];
    if (this.selectedCdConvenio != null) {
      await this.carregarCategorias(Number(this.selectedCdConvenio));
    }
  }

  isSelected(agenda: any): boolean {
    return !this.parceiraSelecionada && this.selectedAgenda?.id === agenda.id;
  }

  /** Agendamento numa agenda de outra unidade (ex.: Mais Laudos, que entra em todas as unidades). */
  unidadeDeOutraAgenda(agenda: any): string | null {
    if (agenda.isParceiro || !agenda.cdEstabelecimentoAgenda || agenda.cdEstabelecimentoAgenda === this.clinicId) return null;
    return agenda.nmUnidadeAgenda || `unidade ${agenda.cdEstabelecimentoAgenda}`;
  }

  get dsSetorSelecionado(): string {
    return this.setores.find(s => s.cdSetorAtendimento === this.cdSetorAtendimento)?.dsSetorAtendimento ?? '';
  }

  // ── Step 2 — Procedimentos ─────────────────────────────────

  async carregarProcedimentoPadrao(): Promise<void> {
    if (this.procedimentosAdicionados.length > 0 || this.parceiraSelecionada) return;
    this.isLoadingPadrao = true;
    try {
      const tipoAgenda = this.selectedAgenda?.classification === 'EXAM' ? 'EXAM' : 'CONSULTATION';
      const isRetorno = tipoAgenda === 'CONSULTATION'
        && this.selectedAgenda?.scheduleClassification === 'R';

      const proc = await this.atendimentoService.getProcedimentoPadrao(
        Number(this.selectedAgenda.id),
        Number(this.selectedAgenda.specialtyId ?? 0),
        tipoAgenda,
        isRetorno,
        this.clinicId,
      );
      if (proc && !this.procedimentosAdicionados.some(p => p.cdProcedimento === proc.cdProcedimento)) {
        this.procedimentosAdicionados.push(this.criarItemProcedimento(proc));
      }
    } catch { /* silencioso — lista pode ficar vazia */ }
    finally { this.isLoadingPadrao = false; }
  }

  private criarItemProcedimento(proc: ProcedimentoBusca, cdMedico: number | null = null, nomeMedico = '') {
    return {
      nome: proc.dsProcExame,
      cdProcedimento: proc.cdProcedimento,
      cdMedicoExecutor: cdMedico,
      nomeMedicoExecutor: nomeMedico,
      quantidade: 1,
      medicoBusca: '',
      medicosFiltrados: [] as Doctor[],
      showMedicoDropdown: false,
    };
  }

  // ── Busca de procedimentos: debounce + guarda de ordem ─────────────────────
  // Sem o debounce era 1 request por tecla; sem a guarda de sequência a resposta
  // de "tir" podia chegar DEPOIS da de "tireoide" e sobrescrever o dropdown.
  filtroTipoBusca: FiltroTipoProcedimento = 'todos';
  private buscaProcTimer: any = null;
  private buscaProcSeq = 0;

  filtrarProcedimentos(): void {
    if (this.buscaProcTimer) clearTimeout(this.buscaProcTimer);
    this.buscaProcTimer = setTimeout(() => this.executarBuscaProcedimentos(), 400);
  }

  setFiltroTipoBusca(filtro: FiltroTipoProcedimento): void {
    this.filtroTipoBusca = filtro;
    if (this.procedimentosBusca.trim().length >= 2) this.executarBuscaProcedimentos();
  }

  private async executarBuscaProcedimentos(): Promise<void> {
    const termo = this.procedimentosBusca.trim();
    if (!termo || termo.length < 2) {
      this.procedimentosFiltrados = [];
      this.showDropdownProcedimentos = false;
      return;
    }
    const seq = ++this.buscaProcSeq;
    this.isLoadingProcedimentos = true;
    try {
      const lista = await this.atendimentoService.buscarProcedimentos(termo, {
        filtro: this.filtroTipoBusca,
        cdConvenio: this.selectedCdConvenio,
        cdCategoria: this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : null,
        cdEstabelecimento: this.clinicId,
        nrSeqAgendamento: this.nrSeqAgendamentoCtx,
        cdAgenda: this.cdAgendaCtx,
      });
      if (seq !== this.buscaProcSeq) return; // resposta atrasada de um termo antigo
      this.procedimentosFiltrados = lista;
      this.showDropdownProcedimentos = lista.length > 0;
    } catch {
      if (seq === this.buscaProcSeq) this.procedimentosFiltrados = [];
    } finally {
      if (seq === this.buscaProcSeq) this.isLoadingProcedimentos = false;
    }
  }

  formatarPrecoProc(proc: ProcedimentoBusca): string {
    if (proc.vlProcedimento == null) return 'sem preço';
    return proc.vlProcedimento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  // ── "Ver tabela": procedimentos do convênio agrupados por categoria ────────
  isTabelaModalOpen = false;
  tabelaLoading = false;
  tabelaClassificacoes: ProcedimentoClassificacao[] = [];
  tabelaClassifSelecionada: ProcedimentoClassificacao | null = null;
  tabelaProcedimentos: ProcedimentoBusca[] = [];
  tabelaBusca = '';
  tabelaSelecionados = new Map<number, ProcedimentoBusca>();
  private tabelaBuscaTimer: any = null;

  async abrirTabelaProcedimentos(): Promise<void> {
    this.isTabelaModalOpen = true;
    this.tabelaClassifSelecionada = null;
    this.tabelaProcedimentos = [];
    this.tabelaBusca = '';
    this.tabelaSelecionados.clear();
    this.tabelaLoading = true;
    try {
      this.tabelaClassificacoes = await this.atendimentoService.getTabelaClassificacoes(
        this.clinicId, this.selectedCdConvenio,
        this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : null,
        { nrSeqAgendamento: this.nrSeqAgendamentoCtx, cdAgenda: this.cdAgendaCtx });
    } catch {
      this.tabelaClassificacoes = [];
    } finally {
      this.tabelaLoading = false;
    }
  }

  fecharTabelaProcedimentos(): void {
    this.isTabelaModalOpen = false;
  }

  async selecionarClassificacaoTabela(classif: ProcedimentoClassificacao): Promise<void> {
    this.tabelaClassifSelecionada = classif;
    await this.carregarProcedimentosTabela();
  }

  voltarParaClassificacoes(): void {
    this.tabelaClassifSelecionada = null;
    this.tabelaProcedimentos = [];
    this.tabelaBusca = '';
  }

  buscarNaTabela(): void {
    if (this.tabelaBuscaTimer) clearTimeout(this.tabelaBuscaTimer);
    this.tabelaBuscaTimer = setTimeout(() => this.carregarProcedimentosTabela(), 400);
  }

  private async carregarProcedimentosTabela(): Promise<void> {
    const termo = this.tabelaBusca.trim();
    if (!this.tabelaClassifSelecionada && termo.length < 2) return;
    this.tabelaLoading = true;
    try {
      this.tabelaProcedimentos = await this.atendimentoService.getTabelaProcedimentos(this.clinicId, {
        nrSeqClassif: this.tabelaClassifSelecionada?.nrSequencia ?? null,
        termo: termo.length >= 2 ? termo : null,
        cdConvenio: this.selectedCdConvenio,
        cdCategoria: this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : null,
      }, { nrSeqAgendamento: this.nrSeqAgendamentoCtx, cdAgenda: this.cdAgendaCtx });
    } catch {
      this.tabelaProcedimentos = [];
    } finally {
      this.tabelaLoading = false;
    }
  }

  toggleTabelaProc(proc: ProcedimentoBusca): void {
    if (this.tabelaSelecionados.has(proc.cdProcedimento)) {
      this.tabelaSelecionados.delete(proc.cdProcedimento);
    } else {
      this.tabelaSelecionados.set(proc.cdProcedimento, proc);
    }
  }

  adicionarSelecionadosTabela(): void {
    for (const proc of this.tabelaSelecionados.values()) {
      if (!this.procedimentosAdicionados.some(p => p.cdProcedimento === proc.cdProcedimento)) {
        this.procedimentosAdicionados.push(this.criarItemProcedimento(proc));
      }
    }
    this.fecharTabelaProcedimentos();
  }

  adicionarProcedimento(proc: ProcedimentoBusca): void {
    if (!this.procedimentosAdicionados.some(p => p.cdProcedimento === proc.cdProcedimento)) {
      this.procedimentosAdicionados.push(this.criarItemProcedimento(proc));
    }
    this.procedimentosBusca = '';
    this.procedimentosFiltrados = [];
    this.showDropdownProcedimentos = false;
  }

  removerProcedimento(index: number): void {
    this.procedimentosAdicionados.splice(index, 1);
  }

  // ── Step 2 — Médico Executor (por item) ────────────────────

  async carregarMedicos(): Promise<void> {
    if (this.todosMedicos.length > 0) return;
    this.isLoadingMedicos = true;
    try {
      this.todosMedicos = await this.doctorService.getDoctors(this.clinicId);
    } catch { /* silencioso */ }
    finally { this.isLoadingMedicos = false; }
  }

  filtrarMedicos(index: number): void {
    const item = this.procedimentosAdicionados[index];
    if (!item) return;
    const termo = item.medicoBusca.trim().toLowerCase();
    if (!termo || termo.length < 2) {
      item.medicosFiltrados = [];
      item.showMedicoDropdown = false;
      return;
    }
    item.medicosFiltrados = this.todosMedicos
      .filter(m => m.doctorName.toLowerCase().includes(termo))
      .slice(0, 8);
    item.showMedicoDropdown = item.medicosFiltrados.length > 0;
  }

  selecionarMedico(index: number, medico: Doctor): void {
    const item = this.procedimentosAdicionados[index];
    if (!item) return;
    item.cdMedicoExecutor = medico.doctorId;
    item.nomeMedicoExecutor = medico.doctorName;
    item.medicoBusca = '';
    item.medicosFiltrados = [];
    item.showMedicoDropdown = false;
  }

  limparMedico(index: number): void {
    const item = this.procedimentosAdicionados[index];
    if (!item) return;
    item.cdMedicoExecutor = null;
    item.nomeMedicoExecutor = '';
    item.medicoBusca = '';
  }

  // ── Step 2 — Orçamento ─────────────────────────────────────

  abrirOrcamentoPagamento(): void {
    this.orcamentoModoPagamento = true;
    this.abrirOrcamento();
  }

  async abrirOrcamento(): Promise<void> {
    this.showOrcamentoModal = true;
    if (this.orcamentoGroups.length > 0) return;

    this.isLoadingOrcamentos = true;
    try {
      const items = await this.atendimentoService.getOrcamentos(this.selectedPassword.personId);
      this.orcamentoGroups = this.agruparOrcamentos(items);
    } catch {
      this.orcamentoGroups = [];
    } finally {
      this.isLoadingOrcamentos = false;
    }
  }

  private agruparOrcamentos(items: OrcamentoItem[]): OrcamentoGroup[] {
    const map = new Map<number, OrcamentoGroup>();
    for (const item of items) {
      if (!map.has(item.nrSequenciaOrcamento)) {
        map.set(item.nrSequenciaOrcamento, {
          nrSequenciaOrcamento: item.nrSequenciaOrcamento,
          dtOrcamento: item.dtOrcamento,
          procedimentos: [],
          totalCartao: 0,
          totalVista: 0,
        });
      }
      const group = map.get(item.nrSequenciaOrcamento)!;
      group.procedimentos.push({ ...item, selecionado: false });
      group.totalCartao += item.vlCartao * item.qtProcedimento;
      group.totalVista += item.vlVista * item.qtProcedimento;
    }
    return Array.from(map.values());
  }

  toggleTodosOrcamento(group: OrcamentoGroup): void {
    const todos = group.procedimentos.every(p => p.selecionado);
    group.procedimentos.forEach(p => p.selecionado = !todos);
  }

  async inserirSelecionados(): Promise<void> {
    if (this.orcamentoModoPagamento) {
      await this.inserirSelecionadosPagamento();
      return;
    }
    for (const group of this.orcamentoGroups) {
      for (const proc of group.procedimentos) {
        if (!proc.selecionado) continue;
        if (this.procedimentosAdicionados.some(a => a.cdProcedimento === proc.cdProcedimento)) continue;
        this.procedimentosAdicionados.push({
          nome: proc.dsProcedimento,
          cdProcedimento: proc.cdProcedimento,
          cdMedicoExecutor: null,
          nomeMedicoExecutor: '',
          quantidade: proc.qtProcedimento,
          medicoBusca: '',
          medicosFiltrados: [],
          showMedicoDropdown: false,
        });
      }
    }
    this.showOrcamentoModal = false;
  }

  private async inserirSelecionadosPagamento(): Promise<void> {
    this.isInserindoOrcamento = true;
    this.pagamentoErro = '';
    try {
      for (const group of this.orcamentoGroups) {
        for (const proc of group.procedimentos) {
          if (!proc.selecionado) continue;
          await this.atendimentoService.adicionarProcedimentoConta({
            nrAtendimento: this.resultado!.nrAtendimento,
            cdPessoaFisica: this.selectedPassword.personId,
            cdEstabelecimento: this.clinicId,
            cdMedico: this.selectedAgenda.doctorId,
            cdSetorAtendimento: this.cdSetorAtendimento,
            cdProcedimento: proc.cdProcedimento,
            nrSequenciaProc: proc.nrSequencia,
            ieOrigemProced: proc.ieOrigemProced ?? 'P',
            quantidade: proc.qtProcedimento,
            nmUsuario: this.userName,
          });
        }
      }
      this.showOrcamentoModal = false;
      this.orcamentoModoPagamento = false;
      // Recarrega tudo
      await this.carregarItensConta();
      await this.recarregarDadosPagamento();
      this.showItensConta = true;
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao adicionar procedimentos do orçamento.';
    } finally {
      this.isInserindoOrcamento = false;
    }
  }

  get totalSelecionadosOrcamento(): number {
    return this.orcamentoGroups.reduce(
      (acc, g) => acc + g.procedimentos.filter(p => p.selecionado).length, 0
    );
  }

  get isParticular(): boolean {
    return this.selectedPassword?.planDescription?.toUpperCase() === 'PARTICULAR';
  }

  fecharOrcamentoModal(): void {
    this.showOrcamentoModal = false;
    this.orcamentoModoPagamento = false;
  }

  todosSelecionados(group: OrcamentoGroup): boolean {
    return group.procedimentos.length > 0 && group.procedimentos.every(p => p.selecionado);
  }

  algumSelecionado(group: OrcamentoGroup): boolean {
    return group.procedimentos.some(p => p.selecionado);
  }

  formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatCupomValor(cupom: CupomDesconto): string {
    switch ((cupom.ieTipoDesconto || '').toUpperCase()) {
      case 'P':
        return cupom.prDesconto != null ? `${cupom.prDesconto}%` : '—';
      case 'V':
        return 'Grátis';
      case 'R':
      case 'F':
        return this.formatCurrency(cupom.valor);
      default:
        return cupom.valor ? this.formatCurrency(cupom.valor) : '—';
    }
  }

  // ── Step 3 — Confirmar e criar ─────────────────────────────

  async criarAtendimento(): Promise<void> {
    if (this.isSubmitting || this.isVerificandoDuplicado) return;
    this.erroMensagem = '';
    this.isVerificandoDuplicado = true;

    try {
      const duplicado = await this.atendimentoService.verificarAtendimentoDuplicado(
        this.selectedPassword.personId,
        this.clinicId,
        this.selectedAgenda.doctorId,
      );

      if (duplicado.existe) {
        this.atendimentoDuplicado = duplicado;
        this.showDuplicadoModal = true;
        return;
      }

      await this.executarCriarAtendimento();
    } catch (error: any) {
      this.erroMensagem = error?.message ?? 'Erro ao verificar atendimento duplicado.';
    } finally {
      this.isVerificandoDuplicado = false;
    }
  }

  fecharDuplicadoModal(): void {
    this.showDuplicadoModal = false;
    this.atendimentoDuplicado = null;
  }

  async confirmarCriarMesmoAssim(): Promise<void> {
    this.showDuplicadoModal = false;
    this.atendimentoDuplicado = null;
    await this.executarCriarAtendimento();
  }

  async executarCriarAtendimento(): Promise<void> {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.erroMensagem = '';

    const procedimentosRequest: ProcedimentoInput[] = this.procedimentosAdicionados.map(p => ({
      cdProcedimento: p.cdProcedimento,
      cdMedicoExecutor: p.cdMedicoExecutor,
      quantidade: p.quantidade,
    }));

    const parceira = ehSelecaoParceira(this.selectedAgenda) ? this.selectedAgenda : null;
    const request: AbrirAtendimentoRequest = {
      nrSeqAgendamento:   parceira ? 0 : Number(this.selectedAgenda.id),
      cdEstabelecimento:  this.clinicId,
      cdSetorAtendimento: this.cdSetorAtendimento!,
      ieTipoAtendimento:  this.tipoAtendimento,
      nmUsuario:          this.userName,
      dsSenhaFila:        this.selectedPassword.dsPassword,
      nrSeqPacSenhaFila:  Number(this.selectedPassword.id),
      procedimentos:      procedimentosRequest.length > 0 ? procedimentosRequest : undefined,
      cdConvenio:         this.selectedCdConvenio != null ? Number(this.selectedCdConvenio) : undefined,
      cdCategoria:        this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : undefined,
      ...(parceira ? {
        cdAgendaParceira: parceira.cdAgendaParceira,
        cdPessoaFisica:   String(this.selectedPassword.personId),
      } : {}),
    };

    try {
      this.resultado = await this.atendimentoService.abrirAtendimentoCompleto(request);
      // Marca que um atendimento foi criado para esta senha (usado ao SAIR da tela
      // para decidir se finaliza — sem quebrar o "Abrir novo atendimento").
      this.atendimentoCriadoNestaSenha = true;
      await this.avancarPagamento();
    } catch (error: any) {
      this.erroMensagem = error?.message ?? 'Erro ao abrir atendimento. Tente novamente.';
    } finally {
      this.isSubmitting = false;
    }
  }

  async ngOnDestroy(): Promise<void> {
    this.buscaAdiantamentos.parar();
    if (this.destaqueAdiantTimer) clearTimeout(this.destaqueAdiantTimer);
    // Ao SAIR da tela (navegacao para outra funcao), se um atendimento foi criado
    // e a senha nao foi finalizada (operador nao clicou "Finalizar senha"), finaliza
    // agora — assim a senha nao fica presa (CHAMADA-ABERTA) reaparecendo na fila.
    // NAO dispara no "Abrir novo atendimento" (que permanece no componente).
    const senhaId = Number(this.selectedPassword?.id);
    if (this.atendimentoCriadoNestaSenha && senhaId && this.senhaFinalizadaId !== senhaId) {
      await this.finalizarSenhaAtendida(senhaId);
    }
  }

  /** Fecha a senha como atendida (answered=0). Best-effort: nao bloqueia o fluxo;
   *  guarda o id para nao repetir. */
  private async finalizarSenhaAtendida(senhaId: number): Promise<void> {
    if (!senhaId || this.senhaFinalizadaId === senhaId) return;
    try {
      await this.passwordService.finishPassword(this.userName, 0, senhaId, this.clinicId);
      this.senhaFinalizadaId = senhaId;
      console.log('[FinalizarSenha] senha finalizada ao sair da tela, id=', senhaId);
    } catch (err) {
      console.error('[FinalizarSenha] falha ao finalizar ao sair, id=', senhaId, err);
      // nao bloqueia; o botao "Finalizar" segue como fallback
    }
  }

  // ── Avaliação Odontológica Gratuita ──

  private async verificarAvaliacaoOdonto(): Promise<boolean> {
    const cdPessoa = Number(this.selectedPassword?.personId ?? this.patientDetails?.id ?? 0);
    const cdEstab  = Number(this.clinicId);
    console.log('[avaliacao-odonto] verificar →', { cdPessoa, cdEstab });
    if (!cdPessoa || !cdEstab) {
      console.warn('[avaliacao-odonto] faltam parâmetros, modal NÃO aparecerá');
      return false;
    }
    try {
      const res = await this.atendimentoService.verificarAvaliacaoOdonto(cdPessoa, cdEstab);
      console.log('[avaliacao-odonto] resposta do backend:', res);
      if (res?.deveExibir && res.agendas?.length > 0) {
        this.agendasOdonto           = res.agendas;
        this.agendaOdontoSelecionada = null; // usuário deve selecionar
        this.showAvaliacaoOdontoModal = true;
        this.modoJustificativaOdonto  = false;
        this.motivoRecusaOdonto       = '';
        this.avaliacaoOdontoErro      = '';
        this.avaliacaoOdontoSucesso   = '';
        console.log('[avaliacao-odonto] MODAL ABERTO, agendas =', this.agendasOdonto);
        return true;
      } else {
        console.warn('[avaliacao-odonto] modal NÃO aparecerá. motivo =', res?.motivo ?? '(sem motivo)');
      }
    } catch (e) {
      console.error('[avaliacao-odonto] erro na verificação:', e);
    }
    return false;
  }

  async aceitarAvaliacaoOdonto(): Promise<void> {
    if (!this.agendaOdontoSelecionada) {
      this.avaliacaoOdontoErro = 'Selecione a agenda para continuar.';
      return;
    }
    const cdPessoa   = Number(this.selectedPassword?.personId ?? this.patientDetails?.id ?? 0);
    const cdConvenio = this.selectedAgenda?.cdConvenio != null ? Number(this.selectedAgenda.cdConvenio) : null;
    const cdCategoria = this.selectedAgenda?.cdCategoria ? String(this.selectedAgenda.cdCategoria) : null;
    const cdEstab = Number(this.clinicId);
    const cdSetor = this.cdSetorAtendimento ? Number(this.cdSetorAtendimento) : null;
    this.isAgendandoOdonto = true;
    this.avaliacaoOdontoErro = '';
    try {
      const res = await this.atendimentoService.agendarAvaliacaoOdonto(
        cdPessoa,
        this.agendaOdontoSelecionada,
        this.userName,
        cdConvenio,
        cdCategoria,
        cdEstab,
        cdSetor,
      );
      if (res.sucesso) {
        this.odontoResult = { nrAtendimento: res.nrAtendimento, nrPrescricao: res.nrPrescricao };
        this.avaliacaoOdontoSucesso = res.mensagem || 'Avaliação odontológica agendada e atendimento aberto com sucesso.';
        this.avaliacaoOdontoConcluida = true;
      } else {
        this.avaliacaoOdontoErro = res.mensagem || 'Não foi possível agendar. Tente novamente.';
      }
    } catch (e: any) {
      this.avaliacaoOdontoErro = 'Não foi possível agendar. Tente novamente.';
    } finally {
      this.isAgendandoOdonto = false;
    }
  }

  recusarAvaliacaoOdonto(): void {
    this.modoJustificativaOdonto = true;
  }

  confirmarRecusaOdonto(): void {
    if (!this.motivoRecusaOdonto.trim()) {
      this.avaliacaoOdontoErro = 'Informe o motivo da recusa.';
      return;
    }
    // MVP: justificativa não é persistida (por decisão do usuário).
    this.avaliacaoOdontoConcluida = true;
    this.fecharAvaliacaoOdontoModal();
  }

  async fecharAvaliacaoOdontoModal(): Promise<void> {
    this.showAvaliacaoOdontoModal = false;
    this.modoJustificativaOdonto  = false;
    this.motivoRecusaOdonto       = '';
    // Continua o fluxo normal → avança para o Step 2 (Procedimentos)
    if (this.stage === 'step1') {
      this.stage = 'step2';
      this.carregarMedicos();
      this.carregarProcedimentoPadrao();
    }
  }

  // ── Pagamento ──────────────────────────────────────────────

  async avancarPagamento(): Promise<void> {
    this.stage = 'pagamento';
    this.pagamentoErro = '';
    this.pagamentoResultado = null;
    this.statusAgenda = 'AR';
    this.isLoadingPagamento = true;
    this.showItensConta = true;
    this.editandoItensConta = true;
    try {
      this.dadosPagamento = await this.atendimentoService.getDadosPagamento(
        this.resultado!.nrAtendimento,
        this.selectedPassword.personId,
      );
      // Já carrega itens e médicos automaticamente
      this.carregarItensConta();
      this.carregarMedicos();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao carregar dados de pagamento.';
    } finally {
      this.isLoadingPagamento = false;
    }
  }

  async processarPagamento(): Promise<void> {
    if (this.isProcessingPagamento || !this.dadosPagamento) return;
    await this.executarProcessarPagamento();
  }

  private async executarProcessarPagamento(): Promise<void> {
    if (this.isProcessingPagamento || !this.dadosPagamento) return;
    this.isProcessingPagamento = true;
    this.pagamentoErro = '';
    try {
      this.pagamentoResultado = await this.atendimentoService.processarPagamento({
        nrInternoConta: this.dadosPagamento.nrInternoConta,
        nrAtendimento: this.resultado!.nrAtendimento,
        cdPessoaFisica: this.selectedPassword.personId,
        cdEstabelecimento: this.clinicId,
        nmUsuario: this.userName,
      });
      // O back já avançou o slot para AC; espelha no select para não exibir AR desatualizado.
      this.statusAgenda = 'AC';
      // Pós-pagamento: se paciente tem adiantamento E conta tem valor, abre modal para vincular
      if (this.dadosPagamento.vlAdiantamento > 0 && this.dadosPagamento.vlTotalConta > 0 && this.pagamentoResultado.nrTitulo) {
        this.vincularCallback = null;
        await this.abrirVincularModal();
      }
      // Carregar resumo de conclusão (step 5)
      await this.carregarResumoConclusao();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao processar pagamento.';
    } finally {
      this.isProcessingPagamento = false;
    }
  }

  private async carregarResumoConclusao(): Promise<void> {
    if (!this.resultado || !this.dadosPagamento) return;
    this.isLoadingResumo = true;
    try {
      this.resumoConclusao = await this.atendimentoService.getResumoConclusao(
        this.resultado.nrAtendimento, this.dadosPagamento.nrInternoConta);
    } catch { /* silencioso */ }
    finally { this.isLoadingResumo = false; }
  }

  async abrirCupomModal(): Promise<void> {
    this.showCupomModal = true;
    this.isLoadingCupons = true;
    this.cupomMensagem = '';
    try {
      this.cuponsDisponiveis = await this.atendimentoService.getCuponsDisponiveis(this.selectedPassword.personId);
    } catch {
      this.cuponsDisponiveis = [];
    } finally {
      this.isLoadingCupons = false;
    }
  }

  fecharCupomModal(): void {
    this.showCupomModal = false;
  }

  async aplicarCupom(cupom: CupomDesconto): Promise<void> {
    if (this.isAplicandoCupom || !this.dadosPagamento) return;
    this.isAplicandoCupom = true;
    this.cupomMensagem = '';
    try {
      await this.atendimentoService.aplicarCupom({
        codCupom: cupom.codCupom,
        nrAtendimento: this.resultado!.nrAtendimento,
        nrInternoConta: this.dadosPagamento.nrInternoConta,
        nmUsuario: this.userName,
      });
      this.cupomMensagemTipo = 'sucesso';
      this.cupomMensagem = `Cupom "${cupom.descCupom}" aplicado com sucesso! Desconto de ${this.formatCurrency(cupom.valor)}.`;
      await this.recarregarDadosPagamento();
      await this.carregarItensConta();
      setTimeout(() => { this.showCupomModal = false; this.cupomMensagem = ''; }, 2000);
    } catch (e: any) {
      this.cupomMensagemTipo = 'erro';
      this.cupomMensagem = e?.error?.message || e?.message || 'Erro ao aplicar cupom. Cupom pode já ter sido utilizado ou não é válido para esta conta.';
    } finally {
      this.isAplicandoCupom = false;
    }
  }

  // ── Maquineta ──────────────────────────────────────────────


  abrirMaquinetaModal(): void {
    this.showMaquinetaModal = true;
    this.maquinetaMetodo = 1;
    this.maquinetaParcelas = 1;
    this.maquinetaValor = this.dadosPagamento?.vlTotalConta || 0;
    this.maquinetaTerminalNumero = '';
    this.maquinetaTerminalFull = '';
    this.aguardandoMaquineta = false;
    const saved = localStorage.getItem('adyen_terminal_id');
    if (saved) {
      this.maquinetaTerminalFull = saved;
      const parts = saved.split('-');
      if (parts.length >= 2) {
        this.maquinetaTerminalTipo = parts[0];
        this.maquinetaTerminalNumero = parts.slice(1).join('-');
      }
    }
  }

  fecharMaquinetaModal(): void {
    this.showMaquinetaModal = false;
    this.aguardandoMaquineta = false;
  }

  get parcelasDisponiveis(): number[] {
    return calcularParcelasDisponiveis(this.maquinetaValor, this.isSuperClinica);
  }

  get parcelaMinimaAtual(): number {
    return parcelaMinima(this.isSuperClinica);
  }

  onMaquinetaValorChange(): void {
    const max = this.parcelasDisponiveis.length;
    if (this.maquinetaParcelas > max) this.maquinetaParcelas = max;
  }

  selecionarTerminalTipo(tipo: string): void {
    this.maquinetaTerminalTipo = tipo;
    this.maquinetaTerminalNumero = serialInicialPorTipo(tipo);
    this.maquinetaTerminalFull = '';
    this.filtrarTerminais();
  }

  filtrarTerminais(): void {
    const termo = this.maquinetaTerminalNumero.trim();
    this.maquinetaTerminaisFiltrados = filtrarTerminaisPorTipo(this.maquinetaTerminalTipo, termo);
    this.showTerminalDropdown = this.maquinetaTerminaisFiltrados.length > 0;
    this.maquinetaTerminalFull = montarTerminalFull(this.maquinetaTerminalTipo, termo);
  }

  selecionarTerminal(t: { tipo: string; numero: string; loja: string }): void {
    this.maquinetaTerminalNumero = t.numero;
    this.maquinetaTerminalFull = `${t.tipo}-${t.numero}`;
    this.showTerminalDropdown = false;
    localStorage.setItem('adyen_terminal_id', this.maquinetaTerminalFull);
  }

  async enviarMaquineta(): Promise<void> {
    if (this.isEnviandoMaquineta || !this.maquinetaTerminalFull || !this.dadosPagamento || this.maquinetaValor <= 0) return;
    this.isEnviandoMaquineta = true;
    this.pagamentoErro = '';
    localStorage.setItem('adyen_terminal_id', this.maquinetaTerminalFull);
    try {
      await this.atendimentoService.cobrarMaquineta({
        metodo: this.maquinetaMetodo,
        valor: this.maquinetaValor,
        parcelas: this.maquinetaParcelas,
        terminal: this.maquinetaTerminalFull,
        descricao: `Atendimento #${this.resultado!.nrAtendimento}`,
        patientId: this.selectedPassword.personId,
        cdEstabelecimento: this.clinicId,
      });
      this.aguardandoMaquineta = true;
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao enviar para maquineta.';
    } finally {
      this.isEnviandoMaquineta = false;
    }
  }

  async finalizarMaquineta(): Promise<void> {
    this.showMaquinetaModal = false;
    this.aguardandoMaquineta = false;
    // Recarrega dados de pagamento e segue no step (para vincular adiantamento, etc.)
    if (this.dadosPagamento) {
      try {
        this.dadosPagamento = await this.atendimentoService.getDadosPagamento(
          this.resultado!.nrAtendimento, this.selectedPassword.personId);
        await this.carregarItensConta();
      } catch { /* silencioso */ }
    }
  }

  async abrirDescontoPepModal(): Promise<void> {
    if (this.dadosPagamento?.vlDesconto && this.dadosPagamento.vlDesconto > 0) {
      this.pagamentoErro = 'Já existe um desconto aplicado nesta conta.';
      return;
    }
    this.showDescontoPepModal = true;
    this.descontoPepMotivo = null;
    this.descontoPepPercentual = null;
    this.descontoPepObs = '';
    if (this.motivosDesconto.length === 0) {
      this.isLoadingMotivos = true;
      try {
        this.motivosDesconto = await this.atendimentoService.getMotivosDesconto();
      } catch { this.motivosDesconto = []; }
      finally { this.isLoadingMotivos = false; }
    }
  }

  fecharDescontoPepModal(): void {
    this.showDescontoPepModal = false;
  }

  async aplicarDescontoPep(): Promise<void> {
    if (this.isAplicandoDesconto || !this.dadosPagamento || !this.descontoPepMotivo || !this.descontoPepPercentual) return;
    if (this.descontoPepPercentual < 0.01 || this.descontoPepPercentual > 15) {
      this.pagamentoErro = 'Percentual deve ser entre 0,01% e 15%.';
      return;
    }
    this.isAplicandoDesconto = true;
    this.pagamentoErro = '';
    try {
      await this.atendimentoService.aplicarDescontoPep({
        nrInternoConta: this.dadosPagamento.nrInternoConta,
        percentual: this.descontoPepPercentual,
        observacao: this.descontoPepObs || undefined,
        nrSeqMotivoDesc: this.descontoPepMotivo,
        nmUsuario: this.userName,
      });
      this.showDescontoPepModal = false;
      await this.recarregarDadosPagamento();
      await this.carregarItensConta();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao aplicar desconto.';
    } finally {
      this.isAplicandoDesconto = false;
    }
  }

  abrirAdicionarProcedimento(): void {
    this.showItensConta = true;
    this.editandoItensConta = true;
    this.adicionandoProcedimento = true;
    this.novoProcBusca = '';
    this.novoProcSelecionado = null;
    this.novoProcFiltrados = [];
    this.novoProcQtd = 1;
    if (this.itensConta.length === 0 && this.dadosPagamento) {
      this.carregarItensConta();
    }
  }

  async filtrarNovoProcedimento(): Promise<void> {
    const termo = this.novoProcBusca.trim();
    if (termo.length < 2) { this.novoProcFiltrados = []; this.showNovoProcDropdown = false; return; }
    try {
      this.novoProcFiltrados = await this.atendimentoService.buscarProcedimentos(termo, {
        cdEstabelecimento: this.clinicId,
        cdConvenio: this.selectedCdConvenio,
        cdCategoria: this.selectedCdCategoria != null ? Number(this.selectedCdCategoria) : null,
        nrAtendimento: this.resultado?.nrAtendimento ?? null,
        cdAgenda: this.cdAgendaCtx,
      });
      this.showNovoProcDropdown = this.novoProcFiltrados.length > 0;
    } catch { this.novoProcFiltrados = []; }
  }

  selecionarNovoProcedimento(proc: ProcedimentoBusca): void {
    this.novoProcSelecionado = proc;
    this.novoProcBusca = proc.dsProcExame;
    this.showNovoProcDropdown = false;
  }

  cancelarAdicionarProcedimento(): void {
    this.adicionandoProcedimento = false;
    this.novoProcSelecionado = null;
    this.novoProcBusca = '';
  }

  async confirmarAdicionarProcedimento(): Promise<void> {
    if (!this.novoProcSelecionado || this.isAdicionandoProc) return;
    this.isAdicionandoProc = true;
    this.pagamentoErro = '';
    try {
      await this.atendimentoService.adicionarProcedimentoConta({
        nrAtendimento: this.resultado!.nrAtendimento,
        cdPessoaFisica: this.selectedPassword.personId,
        cdEstabelecimento: this.clinicId,
        cdMedico: this.selectedAgenda.doctorId,
        cdSetorAtendimento: this.cdSetorAtendimento,
        cdProcedimento: this.novoProcSelecionado.cdProcedimento,
        nrSequenciaProc: this.novoProcSelecionado.nrSequencia,
        ieOrigemProced: this.novoProcSelecionado.ieOrigemProced ?? 'P',
        quantidade: this.novoProcQtd,
        nmUsuario: this.userName,
      });
      this.adicionandoProcedimento = false;
      this.novoProcSelecionado = null;
      this.novoProcBusca = '';
      await this.carregarItensConta();
      await this.recarregarDadosPagamento();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao adicionar procedimento.';
    } finally {
      this.isAdicionandoProc = false;
    }
  }

  private async carregarItensConta(): Promise<void> {
    if (!this.dadosPagamento) return;
    this.isLoadingItens = true;
    try {
      this.itensConta = await this.atendimentoService.getItensContaPaciente(this.dadosPagamento.nrInternoConta);
      for (const item of this.itensConta) {
        item.vlUnitario = item.qtProcedimento > 0 ? item.vlProcedimento / item.qtProcedimento : 0;
      }
    } catch { /* silencioso */ }
    finally { this.isLoadingItens = false; }
  }

  async toggleItensConta(): Promise<void> {
    this.showItensConta = !this.showItensConta;
    if (!this.showItensConta) { this.adicionandoProcedimento = false; }
    if (this.showItensConta && this.itensConta.length === 0) {
      await this.carregarItensConta();
    }
  }

  async alterarQuantidadeItem(item: ItemContaPaciente, delta: number): Promise<void> {
    const novaQtd = item.qtProcedimento + delta;
    if (novaQtd < 1) return;
    try {
      await this.atendimentoService.alterarQuantidadeItem(
        item.nrSequencia, novaQtd, this.dadosPagamento!.nrInternoConta, this.userName);
      item.qtProcedimento = novaQtd;
      await this.recarregarDadosPagamento();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao alterar quantidade.';
    }
  }

  async removerItemConta(item: ItemContaPaciente): Promise<void> {
    if (!confirm('Tem certeza que deseja remover este item da conta?\n\nEsta ação não pode ser desfeita.')) return;
    try {
      await this.atendimentoService.alterarQuantidadeItem(
        item.nrSequencia, 0, this.dadosPagamento!.nrInternoConta, this.userName);
      this.itensConta = this.itensConta.filter(i => i.nrSequencia !== item.nrSequencia);
      await this.recarregarDadosPagamento();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao remover item.';
    }
  }

  toggleEditarMedico(item: ItemContaPaciente): void {
    item.editandoMedico = !item.editandoMedico;
    item.medicoBusca = '';
    item.medicosFiltrados = [];
    item.showMedicoDropdown = false;
  }

  filtrarMedicosItem(item: ItemContaPaciente): void {
    const termo = (item.medicoBusca ?? '').trim().toLowerCase();
    if (termo.length < 2) { item.medicosFiltrados = []; item.showMedicoDropdown = false; return; }
    item.medicosFiltrados = this.todosMedicos
      .filter(m => m.doctorName?.toLowerCase().includes(termo))
      .slice(0, 10)
      .map(m => ({ cdPessoaFisica: m.doctorId, nmGuerra: m.doctorName, isParceiro: m.isParceiro }));
    item.showMedicoDropdown = item.medicosFiltrados!.length > 0;
  }

  async selecionarMedicoItem(item: ItemContaPaciente, medico: { cdPessoaFisica: number; nmGuerra: string; isParceiro?: boolean }): Promise<void> {
    item.showMedicoDropdown = false;
    item.editandoMedico = false;
    try {
      await this.atendimentoService.alterarMedicoExecutor(item.nrSequencia, medico.cdPessoaFisica, this.userName);
      item.nomeMedico = medico.nmGuerra;
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao alterar médico.';
    }
  }

  private async recarregarDadosPagamento(): Promise<void> {
    try {
      this.dadosPagamento = await this.atendimentoService.getDadosPagamento(
        this.resultado!.nrAtendimento, this.selectedPassword.personId);
    } catch { /* silencioso */ }
  }

  // ── Vincular Adiantamento ──────────────────────────────────

  async abrirVincularModal(): Promise<void> {
    if (!this.dadosPagamento) return;
    this.showVincularModal = true;
    this.buscaAdiantamentos.parar();
    this.adiantamentosSelecionados = new Set();
    this.adiantamentosNovos = new Set();
    this.isLoadingAdiantamentos = true;
    this.isVinculando = false;
    this.vincularProgresso = '';
    try {
      this.adiantamentosDisponiveis = await this.atendimentoService.getAdiantamentosDisponiveis(this.dadosPagamento.nrInternoConta);
    } catch {
      this.adiantamentosDisponiveis = [];
    } finally {
      this.isLoadingAdiantamentos = false;
    }
    if (this.showVincularModal) this.buscaAdiantamentos.iniciar();
  }

  async atualizarAdiantamentos(): Promise<void> {
    if (this.atualizandoAdiantamentos || this.buscaAdiantamentos.bloqueioManual > 0) return;
    this.atualizandoAdiantamentos = true;
    try {
      await this.buscaAdiantamentos.atualizarAgora();
    } finally {
      this.atualizandoAdiantamentos = false;
    }
    if (this.showVincularModal) this.buscaAdiantamentos.iniciar();
    this.buscaAdiantamentos.travarManual();
  }

  private aplicarAdiantamentos(lista: AdiantamentoDisponivel[]): void {
    if (!this.showVincularModal || this.isVinculando) return;
    const r = mesclarAdiantamentos(this.adiantamentosDisponiveis, lista, this.adiantamentosSelecionados);
    this.adiantamentosDisponiveis = r.lista;
    this.adiantamentosSelecionados = r.selecionados;
    if (r.novos.length) {
      this.adiantamentosNovos = new Set(r.novos);
      if (this.destaqueAdiantTimer) clearTimeout(this.destaqueAdiantTimer);
      this.destaqueAdiantTimer = setTimeout(() => this.adiantamentosNovos = new Set(), ADIANT_DESTAQUE_MS);
    }
  }

  toggleAdiantamento(nrAdiantamento: number): void {
    if (this.adiantamentosSelecionados.has(nrAdiantamento)) {
      this.adiantamentosSelecionados.delete(nrAdiantamento);
    } else {
      this.adiantamentosSelecionados.add(nrAdiantamento);
    }
  }

  isAdiantamentoSelecionado(nrAdiantamento: number): boolean {
    return this.adiantamentosSelecionados.has(nrAdiantamento);
  }

  get totalAdiantamentosSelecionados(): number {
    return this.adiantamentosSelecionados.size;
  }

  get valorAdiantamentosSelecionados(): number {
    return this.adiantamentosDisponiveis
      .filter(a => this.adiantamentosSelecionados.has(a.nrAdiantamento))
      .reduce((sum, a) => sum + a.vlSaldo, 0);
  }

  async confirmarVinculacao(): Promise<void> {
    if (this.isVinculando || this.adiantamentosSelecionados.size === 0) return;
    // Usa nrTitulo do resultado do pagamento (mais confiável)
    const nrTitulo = this.pagamentoResultado?.nrTitulo || this.dadosPagamento?.nrTituloConta;
    if (!nrTitulo) return;
    this.buscaAdiantamentos.parar();
    this.isVinculando = true;
    this.pagamentoErro = '';
    const selecionados = this.adiantamentosDisponiveis.filter(a => this.adiantamentosSelecionados.has(a.nrAdiantamento));
    let vinculados = 0;
    try {
      for (const adian of selecionados) {
        vinculados++;
        this.vincularProgresso = `Vinculando ${vinculados}/${selecionados.length}...`;
        await this.atendimentoService.vincularAdiantamento({
          nrTitulo: nrTitulo,
          nrAdiantamento: adian.nrAdiantamento,
          nmUsuario: this.userName,
        });
      }
      this.showVincularModal = false;
      // Se tem callback (pré-vincular → pagamento), executar
      if (this.vincularCallback) {
        const cb = this.vincularCallback;
        this.vincularCallback = null;
        await cb();
        return;
      }
      // Pós-vincular: recarregar resumo para refletir adiantamentos
      await this.carregarResumoConclusao();
    } catch (e: any) {
      this.pagamentoErro = e?.message ?? 'Erro ao vincular adiantamento.';
      if (this.showVincularModal) this.buscaAdiantamentos.iniciar();
    } finally {
      this.isVinculando = false;
      this.vincularProgresso = '';
    }
  }

  fecharVincularModal(): void {
    this.buscaAdiantamentos.parar();
    this.showVincularModal = false;
    // Se cancelou o pré-vincular, processar pagamento mesmo assim (igual PRE)
    if (this.vincularCallback) {
      const cb = this.vincularCallback;
      this.vincularCallback = null;
      cb();
    }
  }

  async verFichaPdf(): Promise<void> {
    if (!this.dadosPagamento) return;
    const ficha = await this.atendimentoService.getFichaAtendimento(this.dadosPagamento.nrInternoConta);
    if (!ficha) { this.pagamentoErro = 'Erro ao carregar ficha.'; return; }
    // Gerar barcode
    let barcodeDataUrl = '';
    try {
      const JsBarcode = (await import('jsbarcode')).default;
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, String(ficha.nrAtendimento), {
        format: 'CODE128', width: 1, height: 40, displayValue: false, margin: 0
      });
      barcodeDataUrl = canvas.toDataURL();
    } catch { /* silencioso */ }
    this.imprimirFicha(ficha, barcodeDataUrl);

    // Identificar e imprimir TCLEs correspondentes aos procedimentos
    const tcles = matchTcles(ficha.procedimentos ?? []);
    this.tclesEncontrados = tcles;
    tcles.forEach((tcle, i) => {
      setTimeout(() => {
        const handled = tcle.key ? imprimirTcle(tcle.key as TcleKey, ficha, this.userName, { logo: this.brandService.logo, cdEmpresa: this.brandService.cdEmpresa }) : false;
        if (!handled) window.open(urlTcle(tcle.driveId), '_blank');
      }, 800 + i * 800);
    });
  }

  private imprimirFicha(f: any, barcodeDataUrl: string = ''): void {
    const fc = (v: number) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '';
    const logo = fichaLogoHtml(this.brandService.logo);
    const titulo = fichaTitulo(this.brandService.brandName);

    // Cancela negativos: agrupa por cdProcedimento+executor, soma qtd, descarta se ≤ 0
    const qtdMap = new Map<string, { proc: any; qtTotal: number }>();
    for (const proc of (f.procedimentos || [])) {
      const key = `${proc.cdProcedimento}|${proc.nmMedicoExecutor || ''}`;
      if (!qtdMap.has(key)) {
        qtdMap.set(key, { proc, qtTotal: 0 });
      }
      qtdMap.get(key)!.qtTotal += Number(proc.qtProcedimento) || 0;
    }
    const procsNormalizados = [...qtdMap.values()]
      .filter(e => e.qtTotal > 0)
      .map(e => ({ ...e.proc, qtProcedimento: e.qtTotal }));

    // Agrupar procedimentos por médico executor (igual ao PRE)
    const gruposMap = new Map<string, { nmMedico: string; procs: any[] }>();
    for (const proc of procsNormalizados) {
      const key = proc.nmMedicoExecutor || '__SEM_EXECUTOR__';
      if (!gruposMap.has(key)) {
        gruposMap.set(key, { nmMedico: proc.nmMedicoExecutor || f.nmMedico || '', procs: [] });
      }
      gruposMap.get(key)!.procs.push(proc);
    }
    const gruposArr = [...gruposMap.values()];
    if (gruposArr.length === 0) {
      gruposArr.push({ nmMedico: f.nmMedico || '', procs: [] });
    }

    // Gera uma página por grupo de médico (igual ao PRE)
    const renderPagina = (grupo: { nmMedico: string; procs: any[] }, isFirst: boolean) => {
      const pageBreak = isFirst ? '' : 'page-break-before: always;';
      const procsHtml = grupo.procs.map((p: any) => `
        <tr>
          <td class="col-codigo">${p.cdProcedimento || ''}</td>
          <td class="col-desc">${p.dsProcedimento || ''}</td>
          <td class="col-qtd">${p.qtProcedimento || ''}</td>
          <td class="col-vlprazo">${fc(p.vlPrazo)}</td>
          <td class="col-vlvista"></td>
          <td class="col-dtexec"></td>
          <td class="col-obs"></td>
          <td class="col-lado"></td>
        </tr>`).join('');

      return `
      <div style="${pageBreak}">
        <div class="header-container">
          <div class="header-left"><div class="logo">${logo}</div></div>
          <div class="header-title"><h1>${titulo}</h1><h2>Exames/Procedimentos</h2></div>
          <div class="barcode-right">${barcodeDataUrl ? `<img src="${barcodeDataUrl}" alt="Barcode" />` : ''}</div>
        </div>
        <table class="info-table">
          <tr><td class="label">Prontuário</td><td>${f.nrProntuario || ''}</td><td class="label">Atendimento</td><td>${f.nrAtendimento || ''}</td><td class="label">Prescrição</td><td>${f.nrPrescricao || ''}</td><td class="label">Data Entrada</td><td>${f.dtEntrada || ''}</td></tr>
          <tr><td class="label">Paciente</td><td colspan="3">${f.nmPaciente || ''}</td><td class="label">CPF</td><td>${this.formatarCpf(f.cpf)}</td><td class="label">Telefone</td><td>${f.telefone || ''}</td></tr>
          <tr><td class="label">Data Nascto</td><td>${f.dtNascimento || ''}</td><td class="label">Idade</td><td>${f.dsIdade || ''}</td><td class="label">Sexo</td><td colspan="3">${f.dsSexo || ''}</td></tr>
          <tr><td class="label">Endereço</td><td colspan="3">${f.dsEndereco || ''}</td><td class="label">Nº</td><td>${f.nrEndereco || ''}</td><td class="label">Data Entrega</td><td>${f.dtEntrega || ''}</td></tr>
          <tr><td class="label">Convênio</td><td colspan="3">${f.nmConvenio || ''}</td><td class="label">Bairro</td><td>${f.bairro || ''}</td><td class="label">Usuário Aten.</td><td>${f.nmUsuarioAtend || ''}</td></tr>
          <tr><td class="label">Setor</td><td>${f.dsSetorLeito || ''}</td><td class="label">CEP</td><td>${f.cep || ''}</td><td class="label">Data Lib.</td><td colspan="3">${f.dtLiberacao || ''}</td></tr>
          <tr><td class="label"></td><td></td><td class="label"></td><td></td><td class="label">Estado</td><td>${f.estado || ''}</td><td class="label">Estab.</td><td>${f.estabelecimento || ''}</td></tr>
          <tr><td class="label">E-mail:</td><td colspan="3">${f.email || ''}</td><td class="label"></td><td></td><td class="label">CNPJ:</td><td>${f.cnpj || ''}</td></tr>
        </table>
        <div class="responsavel-row">
          <span class="field"><span class="label">Responsável:</span> ${f.nmResponsavel || ''}</span>
          <span class="field"><span class="label">CPF:</span> ${this.formatarCpf(f.cpfResponsavel)}</span>
          <span class="field"><span class="label">E-mail:</span> ${f.emailResponsavel || ''}</span>
        </div>
        <div class="section-title">Atendimento Atual</div>
        <div class="atendimento-box">
          <div class="atendimento-field"><span class="label">Médico:</span> ${grupo.nmMedico}</div>
          <div class="atendimento-field"><span class="label">Forma de Entrega do Laudo:</span> </div>
          <div class="atendimento-field"><span class="label">Diagnóstico:</span> </div>
        </div>
        ${procsHtml ? `
        <div class="exames-title">Exames Solicitados</div>
        <table class="proc-table">
          <thead><tr>
            <th class="col-codigo">Código</th><th class="col-desc">Descrição</th><th class="col-qtd">Qtde.</th>
            <th class="col-vlprazo">Vl a prazo</th><th class="col-vlvista">Vl a vista</th>
            <th class="col-dtexec">Data e Hora Exec.</th><th class="col-obs">Observação</th><th class="col-lado">Lado</th>
          </tr></thead>
          <tbody>${procsHtml}</tbody>
        </table>` : ''}
        <div class="footer">
          <p><strong>Impresso por:</strong> ${this.userName} | <strong>Data/Hora:</strong> ${new Date().toLocaleString('pt-BR')} | <strong>Estabelecimento:</strong> ${f.estabelecimento || 'N/A'}</p>
        </div>
      </div>`;
    };

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${titulo}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; font-size: 9px; color: #000; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  @media print { body { margin: 10px; } @page { size: A4; margin: 20px; } .no-print { display: none; } }
  .header-container { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
  .header-left { display: flex; align-items: center; }
  .header-left .logo { margin-right: 20px; }
  .header-left .logo svg { height: 50px; width: auto; }
  .header-left .logo img { height: 50px; width: auto; }
  .header-title { text-align: center; flex-grow: 1; }
  .header-title h1 { font-size: 16px; font-weight: bold; margin: 0; }
  .header-title h2 { font-size: 11px; margin: 3px 0 0 0; font-weight: normal; }
  .barcode-right { text-align: right; }
  .info-table { width: 100%; border-collapse: collapse; margin-bottom: 8px; border: 1px solid #000; }
  .info-table td { padding: 3px 5px; vertical-align: middle; font-size: 9px; border: 1px solid #000; }
  .info-table .label { font-weight: bold; background-color: #e8e8e8; padding: 3px 5px; }
  .responsavel-row { margin-bottom: 10px; padding: 3px 5px; border: 1px solid #000; background-color: #f5f5f5; }
  .responsavel-row .field { display: inline-block; margin-right: 40px; }
  .responsavel-row .label { font-weight: bold; }
  .section-title { font-weight: bold; text-align: center; padding: 5px; border-top: 1px solid #000; border-bottom: 1px solid #000; margin: 10px 0 5px 0; font-size: 10px; background-color: #f5f5f5; }
  .atendimento-box { border: 1px solid #000; padding: 8px; margin-bottom: 8px; }
  .atendimento-field { margin-bottom: 3px; }
  .atendimento-field .label { font-weight: bold; }
  .exames-title { font-weight: bold; text-align: center; padding: 5px; border-top: 1px solid #000; border-bottom: 1px solid #000; margin: 10px 0 5px 0; font-size: 10px; background-color: #f5f5f5; }
  .proc-table { width: 100%; border-collapse: collapse; margin-top: 5px; }
  .proc-table th { background-color: #e8e8e8; border: 1px solid #000; padding: 3px 5px; font-size: 8px; text-align: left; font-weight: bold; }
  .proc-table td { border: 1px solid #000; padding: 3px 5px; font-size: 8px; vertical-align: top; }
  .proc-table .col-codigo { width: 70px; }
  .proc-table .col-desc { }
  .proc-table .col-qtd { width: 35px; text-align: center; }
  .proc-table .col-vlprazo { width: 60px; text-align: right; }
  .proc-table .col-vlvista { width: 60px; text-align: right; }
  .proc-table .col-dtexec { width: 110px; text-align: center; }
  .proc-table .col-obs { width: 80px; }
  .proc-table .col-lado { width: 40px; text-align: center; }
  .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #000; text-align: center; font-size: 8px; color: #666; }
  .footer p { margin: 0; }
</style></head><body>
${gruposArr.map((grupo, idx) => renderPagina(grupo, idx === 0)).join('')}
</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 500);
    }
  }

  sumVlOriginal = (acc: number, a: any) => acc + (a.vlOriginal || 0);
  sumVlSaldoAtual = (acc: number, a: any) => acc + (a.vlSaldoAtual || 0);

  async pularPagamento(): Promise<void> {
    if (!this.dadosPagamento || !this.resultado) {
      this.voltarDashboard();
      return;
    }
    // Se tem agenda vinculada, perguntar se quer alterar status
    if (this.selectedAgenda?.id) {
      this.showPularPagamentoModal = true;
      return;
    }
    this.executarPularPagamento();
  }

  private get tpAgendaSelecionada(): 'EXAM' | 'CONSULTATION' {
    return this.selectedAgenda?.classification === 'EXAM' ? 'EXAM' : 'CONSULTATION';
  }

  async confirmarPularPagamento(alterarStatus: boolean): Promise<void> {
    this.showPularPagamentoModal = false;
    if (alterarStatus && this.selectedAgenda?.id) {
      try {
        await this.atendimentoService.atualizarStatusAgenda(
          Number(this.selectedAgenda.id), this.userName, 'AC', this.tpAgendaSelecionada);
        this.statusAgenda = 'AC';
        console.log(`[pularPagamento] status AC gravado seq=${this.selectedAgenda.id}`);
      } catch (e) {
        // não bloqueia o fluxo, mas precisa deixar rastro: o Tasy segue pendente
        console.error(`[pularPagamento] falhou ao gravar AC seq=${this.selectedAgenda.id}`, e);
      }
    }
    this.executarPularPagamento();
  }

  private async executarPularPagamento(): Promise<void> {
    this.pagamentoPulado = true;
    this.pagamentoResultado = {
      nrTitulo: this.dadosPagamento!.nrTituloConta || '',
      vlContaFinal: this.dadosPagamento!.vlTotalConta,
    };
    await this.carregarResumoConclusao();
  }

  async alterarStatusAgenda(): Promise<void> {
    if (!this.selectedAgenda?.id) return;
    this.statusAgendaFeedback = 'Salvando...';
    try {
      await this.atendimentoService.atualizarStatusAgenda(
        Number(this.selectedAgenda.id), this.userName, this.statusAgenda, this.tpAgendaSelecionada);
      this.statusAgendaFeedback = 'Salvo!';
    } catch {
      this.statusAgendaFeedback = 'Erro ao salvar';
    }
    setTimeout(() => this.statusAgendaFeedback = '', 2000);
  }

  copiarValor(campo: string, valor: any): void {
    navigator.clipboard.writeText(String(valor ?? '')).then(() => {
      this.copiadoCampo = campo;
      setTimeout(() => this.copiadoCampo = '', 2000);
    });
  }

  // ── Step indicator helpers ──────────────────────────────────

  private get stageNum(): number {
    if (this.stage === 'pagamento' && this.pagamentoResultado) return 5;
    switch (this.stage) {
      case 'step1': return 1;
      case 'step2': return 2;
      case 'step3': return 3;
      case 'pagamento': return 4;
      default: return 0;
    }
  }

  stepAtivo(step: number): boolean {
    return step <= this.stageNum;
  }

  stepCompleto(step: number): boolean {
    return step < this.stageNum;
  }

  stepAtual(step: number): boolean {
    return step === this.stageNum;
  }

  linhaAtiva(i: number): boolean {
    return i + 1 < this.stageNum;
  }

  // ── Helpers ────────────────────────────────────────────────

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR');
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  formatarCpf(cpf: string): string {
    const d = (cpf ?? '').replace(/\D/g, '');
    if (d.length !== 11) return cpf || '—';
    return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
  }

  get tipoAtendimentoLabel(): string {
    return this.tipoAtendimento === 8 ? 'Atendimento Ambulatorial' : String(this.tipoAtendimento);
  }

  get classificacaoLabel(): string {
    if (this.classificacao === 'E') return 'Exames';
    return this.classificacao === 'C' ? 'Consulta' : 'Retorno';
  }
}
